import { isContractor, orgInfo } from '../org.js';
import { canAssets } from '../assets-access.js';
import { allowedDomainsOf, checkWorkEmail } from '../sign-in-domains.js';
import { allUsers, allProjects, ACTIVE_STATUSES, invalidateCache, updateUserFields, financeRates, setFinanceRate, financeSalaries, setUserSalary, allTimesheets, listLeaveRequests, addTeamMember, grantAccess, addRecordChange, listRecordChanges, hrRecords, setHrRecord, addFinanceLogEntry, orgSignatureConfig } from '../db.js';
import { escapeHtml, refTail, initials, titleCase, avatarName, skeleton, statusCell, pill, secretFig } from '../ui.js';
import { openOverlay } from './overlay.js';
import { leaveBalance, leaveOnDay, isoOf, ENTITLEMENT_DAYS, leaveWord } from '../leave.js';
import { bmOpen, bmBrief, bmTable, bmTableNav, bmFiguresCard } from '../bm.js';
import { openRowMenu } from './row-menu.js';
import { renderPill } from './enum-pill.js';
import { openEndingConfirm } from './ending-confirm.js';
import { openDialog, openPanel } from './dialog.js';
import { renderSignatureTab } from './signature.js';
import { mountSignatureCard } from './signature-settings.js';
import { toE164 } from '../signature-render.js';
import { ukMobile } from '../uk-mobile.js';
import { stepUpFresh, ensureStepUp } from '../stepup.js';
import { renderRoomTab } from './room-settings.js';
import { orgSetting } from '../org.js';
import { contractMinutes } from '../contract-hours.js';
import { isTimesheetExempt } from '../timesheet-required.js';
import { isPerson, isRoomScreen } from '../people.js';
import { onAspHost } from '../auth.js';
import { callFunction } from '../db.js';
import { hasFeature } from '../features.js';
import { loadKit, kitLine, collectLines } from '../data/assets-team.js';   // Assets mission 4: Kit (read-only)

// Team — the people page. v4.0.0 (design export 22/10/2026, p23): a page head
// with an inline subtitle, underline tabs (People · Signature · Meeting room ·
// Calendar · CPD, v4.23.0), a
// ruled four-figure strip, a one-line pay lock, then the roster on hairlines
// (avatar, name over job title, leads, utilisation against the target tick, leave
// left, signature, and — seniors only — Charge-out + Cost). Every write path and
// the person page are carried over unchanged. CRITICAL role-gating preserved:
// salary, cost rate and HR records need Owner/Director/Finance AND a fresh
// step-up — they are not fetched while locked, and their cells are dots; once
// unlocked, money goes through secretFig (the figures shield). Non-seniors never
// fetch the rates (data layer) nor get the Charge-out/Cost columns (render layer).

const SENIOR = ['Owner', 'Director', 'Finance'];
const LEADERSHIP = ['Owner', 'Director', 'Finance'];
const ASP_COLOURS = ['#0A40C0', '#10328B', '#0C1139', '#FF5001', '#C9A227', '#1F7A4D', '#8A8B85'];
// A person's expected week: their own, else the company's (ASP 37:30); with
// neither there is nothing to measure utilisation against, so it reads 0.
const weekMinOf = (u) => contractMinutes(u, orgSetting('weeklyContractMinutes', null));
// The org's utilisation target (settings.utilisationTarget; ASP 0.9) — ≥ this → orange.
// What signing in takes here: the company's domains (ASP: "an aspconsulting.co.uk
// sign-in"), or an invite for a company that lists none.
const accessNeeds = () => {
  const d = allowedDomainsOf({ signIn: orgSetting('signIn', null), signInDomain: orgSetting('signInDomain', null) });
  return d.length ? `an ${d.join(' or ')} sign-in` : 'an invite';
};
const overload = () => Math.round((Number(orgSetting('utilisationTarget', 0.9)) || 0.9) * 100);
const parseHM = (s) => {
  const t = String(s || '').trim(); if (!t) return 0;
  if (t.includes(':')) { const [h, m] = t.split(':'); return (Number(h) || 0) * 60 + (Number(m) || 0); }
  return Math.round((Number(t) || 0) * 60);
};
const GBP = (n) => '£' + Number(n).toLocaleString('en-GB');
const roleGroup = (u) => {
  const rs = u.roles || [];
  if (rs.some((r) => LEADERSHIP.includes(r))) return 'Leadership';
  if (rs.includes('Coordinator')) return 'Coordinators';
  if (rs.includes('Technician')) return 'Technicians';
  return 'Engineers';
};
const utilColour = (u) => (u >= overload() ? 'var(--brand-orange)' : u >= 55 ? 'var(--brand-blue)' : 'var(--hair-strong)');
const firstName = (n) => titleCase(String(n || '').split(' ')[0] || '');
const byName = (a, b) => titleCase(a.displayName || a.id).localeCompare(titleCase(b.displayName || b.id));

// ── The page's two tabs ─────────────────────────────────────────────────────
// The roster is the Team page and stays exactly as it was; Signature is a
// second view of the same people — what every one of their emails says
// underneath it (Karan 07/09). Only the roles that may push see the tab at all,
// which matches the rules rather than the Team page's wider "senior" idea:
// Finance may set what somebody is paid and not what the practice says in its
// own name.
/**
 * Grant this person's access now, and say what actually happened.
 *
 * Adding somebody used to write a record and stop there; the record admits
 * nobody, the CLAIM does, and the only thing minting claims read the V1
 * spreadsheet. So a person added here waited on a provisioning screen forever
 * (Karan 18/09). Every add and every role change now writes the claim too.
 *
 * "Awaiting first sign-in" is the normal answer for somebody brand new — there
 * is no Firebase Auth account to attach a claim to until they have signed in
 * once, and the provisioning screen finishes the job itself at that moment.
 * It is reported rather than hidden so nobody is left wondering.
 */
async function announceAccess(userId, email) {
  const who = String(email || '').split('@')[0] || 'They';
  let text;
  try {
    const res = await grantAccess(userId);
    if (res && res.state === 'granted') text = `${who} can sign in now.`;
    else if (res && res.state === 'revoked') text = `${who}'s access has been removed.`;
    else text = `${who} needs to sign in to Track once — their access completes by itself the moment they do.`;
  } catch (e) {
    text = `Saved, but their access could not be set: ${e && e.message ? e.message : e}`;
  }
  const n = document.createElement('div');
  n.setAttribute('role', 'status');
  n.textContent = text;
  Object.assign(n.style, {
    position: 'fixed', left: '50%', transform: 'translateX(-50%)', top: '18px',
    background: 'var(--brand-navy)', color: 'var(--brand-white)', fontSize: '13px', lineHeight: '1.4',
    padding: '11px 16px', borderRadius: '10px', maxWidth: 'min(520px, calc(calc(100 * var(--vw)) - 32px))',
    boxShadow: '0 12px 32px rgba(12,17,57,0.18)', zIndex: '900',
  });
  document.body.appendChild(n);
  setTimeout(() => {
    n.style.transition = 'opacity 200ms var(--ease-out)';
    n.style.opacity = '0';
    setTimeout(() => n.remove(), 220);
  }, 5000);
}

const SIGNATURE_ROLES = ['Owner', 'Director'];
const TAB_OFF = 'border:1px solid var(--hair);border-radius:9px;padding:8px 15px;min-height:38px;background:var(--surface);font-family:inherit;font-size:12.5px;color:var(--ink);cursor:pointer';
const TAB_ON = 'border:1px solid var(--ink);border-radius:9px;padding:8px 15px;min-height:38px;background:var(--ink-fill);font-family:inherit;font-size:12.5px;color:var(--ink-fill-ink);cursor:pointer';
// Module-level, so a write that re-renders the page (a saved edit, a push) comes
// back to the tab you were on rather than throwing you to the roster.
let activeTab = 'Team';
// Set by renderTeam so the roster's "Deploy signatures" can hand over to the
// Signature tab without knowing how the tab strip is wired.
let showTab = null;
/** Switch the Team screen to a named tab, from anywhere inside it. */
export function openSignatureTab() { if (showTab) showTab('Signature'); }

export async function renderTeam(mount, session, opts = {}) {
  // A contractor company's Team is who can approve work and what people cost
  // (Traverse, board LPeople) — a component, not a copy of this screen.
  if (isContractor()) {
    const { renderReviewers } = await import('./team-reviewers.js');
    return renderReviewers(mount, session);
  }
  if (!(session.roles || []).some((r) => SIGNATURE_ROLES.includes(r))) {
    activeTab = 'Team';
    return renderRoster(mount, session, opts);
  }
  // v4.0.0 (p23): the page head sits above underline tabs — People · Signature ·
  // Meeting room. The roster fills the head (its subtitle and buttons need its
  // data); the other two tabs show the title alone.
  mount.innerHTML = '<div class="tm4-headslot"></div><div class="tabs tm-tabs" role="tablist" aria-label="Team"></div><div class="tm-panel"></div>';
  const headEl = mount.querySelector('.tm4-headslot');
  const bar = mount.querySelector('.tm-tabs');
  const panel = mount.querySelector('.tm-panel');
  const LABEL = { Team: 'People', Signature: 'Signature', Room: 'Meeting room', CPD: 'CPD' };
  const show = (next) => {
    if (next) activeTab = next;
    bar.innerHTML = ['Team', 'Signature', 'Room', 'CPD']
      .map((t) => `<button type="button" role="tab" class="tab${t === activeTab ? ' active' : ''}" aria-selected="${t === activeTab}" data-tab="${t}">${LABEL[t]}</button>`).join('');
    bar.querySelectorAll('[data-tab]').forEach((b) => b.addEventListener('click', () => {
      if (b.dataset.tab === activeTab) return;
      activeTab = b.dataset.tab;
      show();
    }));
    headEl.innerHTML = '<div class="tm4-head"><div class="page-h"><h1>Team</h1></div></div>';
    // Each tab owns the panel completely — no shared state to get out of step.
    if (activeTab === 'Signature') renderSignatureTab(panel, session);
    else if (activeTab === 'Room') renderRoomTab(panel, session);
    // CPD (v4.23.0): each person's hours this CPD year against the target (Owner and Directors).
    else if (activeTab === 'CPD') import('./cpd-team.js').then((m) => m.renderCpdTeam(panel, session));
    else renderRoster(panel, session, { ...opts, headEl });
  };
  showTab = show;
  show();
}

async function renderRoster(mount, session, { onOpenLead, onOpenProject, editPatterns = false, headEl = null } = {}) {
  const isSenior = session.roles.some((r) => SENIOR.includes(r));
  // Marking someone former writes `active`, which firestore.rules gives to
  // Owners and Directors only (never Finance) — so only they are offered it.
  const canOffboard = session.roles.some((r) => r === 'Owner' || r === 'Director');
  // ── Edit-patterns pilot gate ──────────────────────────────────────────────
  // opts.editPatterns (passed by the app; DEFAULT FALSE) turns on the inline
  // EDIT affordance on the person record, the "Changes to this record" list, and
  // the ⋯ row menu on the roster. When false the page renders EXACTLY as today —
  // no new affordances. Within it, the charge-out field is still role-gated on
  // isSenior (§6: Charge-out is Owner · Director · Finance).
  // §1.2 date eyebrow — today, en-GB long (e.g. "Monday 4 August 2026").
  mount.innerHTML = `<div id="tm-loading">${skeleton()}</div>`;

  let users = [], projects = [], rates = {}, salaries = {}, hrs = {}, sheets = [], leaveReqs = [], sigCfg = null;
  // Pay, cost rates and the employment record need a fresh biometric check
  // (security review 17/10/2026) — the rules refuse them without one, so they
  // are fetched only once unlocked, and the page offers the unlock.
  const payUnlocked = isSenior && await stepUpFresh();
  if (!mount.__payUnlockWired) {
    mount.__payUnlockWired = true;
    mount.addEventListener('click', async (ev) => {
      if (!ev.target.closest('[data-pay-unlock]')) return;
      if (await ensureStepUp()) renderRoster(mount, session, { onOpenLead, onOpenProject, editPatterns, headEl });
    });
  }
  try {
    // Role-gate at the DATA layer: non-seniors never fetch rates or salaries.
    [users, projects, rates, salaries, hrs, sheets, leaveReqs] = await Promise.all([
      allUsers(), allProjects().catch(() => []),
      payUnlocked ? financeRates().catch(() => ({})) : Promise.resolve({}),
      payUnlocked ? financeSalaries().catch(() => ({})) : Promise.resolve({}),
      payUnlocked ? hrRecords().catch(() => ({})) : Promise.resolve({}),
      allTimesheets().catch(() => []),
      listLeaveRequests().catch(() => []),
    ]);
    // The signature template's own version (§10). Owner-readable; anyone else
    // gets null and the column simply is not drawn.
    sigCfg = await orgSignatureConfig().catch(() => null);
  } catch (e) {
    const l = mount.querySelector('#tm-loading');
    if (l) l.innerHTML = `<p class="bm-body error" style="padding:16px 4px">Couldn’t load: ${escapeHtml(e && e.message || e)}</p>`;
    return;
  }

  /**
   * SIGNATURE STATE (§10) — Current · v3 / Outdated · v2 / Not set.
   *
   * Read from what the push function records on the person: the version of the
   * template that was last written into their Gmail. A person who has never
   * been pushed to is "Not set", and so is one whose last push predates the
   * version counter — because "Outdated · v0" would be a number Track invented.
   */
  const sigVersion = Number(sigCfg && sigCfg.version) || 0;
  const signatureState = (u) => {
    const sig = u.signature || {};
    if (sig.lastError) return { label: 'Failed', tone: 'var(--brand-orange)', attn: true, cls: 'bad' };
    const v = Number(sig.lastPushedVersion) || 0;
    // "Not set" does NOT flag the row. Until the first deployment that is
    // everybody, and eleven rows all carrying the orange attention rule is not
    // an alert, it is wallpaper — the rule has to mean something is wrong with
    // THIS row. Outdated and Failed do; never-yet-deployed does not.
    if (!sig.lastPushedAt || !v) return { label: 'Not set', tone: 'var(--ink-3)', attn: false, cls: 'unset' };
    if (v >= sigVersion) return { label: `Current \u00b7 v${v}`, tone: 'var(--status-ok-ui)', attn: false, cls: 'ok' };
    return { label: `Outdated \u00b7 v${v}`, tone: 'var(--brand-orange)', attn: true, cls: 'bad' };
  };

  // Live projects led, per person.
  const liveByLead = {};
  for (const p of projects) {
    if (!ACTIVE_STATUSES.includes(p.status)) continue;
    const id = refTail(p.lead);
    if (id) liveByLead[id] = (liveByLead[id] || 0) + 1;
  }

  // Utilisation — tracked minutes over the 4 most recent weeks ÷ (4 × 37:30).
  const now = new Date();
  const mondayOf = (d) => { const x = new Date(d); const g = (x.getDay() + 6) % 7; x.setDate(x.getDate() - g); x.setHours(0, 0, 0, 0); return x; };
  const thisMon = mondayOf(now);
  const winWeeks = [3, 2, 1, 0].map((k) => { const m = new Date(thisMon); m.setDate(m.getDate() - k * 7); return isoOf(m); });
  const winSet = new Set(winWeeks);
  const minsByEmail = {};
  for (const s of sheets) {
    if (s.imported || !winSet.has(s.weekStart)) continue;
    const who = String(s.userEmail || '').toLowerCase(); if (!who) continue;
    let mins = 0; (s.rows || []).forEach((r) => Object.values(r.days || {}).forEach((v) => { mins += parseHM(v); }));
    minsByEmail[who] = (minsByEmail[who] || 0) + mins;
  }
  const utilOf = (u) => (weekMinOf(u) ? Math.round(((minsByEmail[String(u.email || '').toLowerCase()] || 0) / (4 * weekMinOf(u))) * 100) : 0);
  const todayIso = isoOf(now);
  const onLeaveToday = (u) => !!leaveOnDay(leaveReqs, u.email, todayIso);
  const leaveLeft = (u) => leaveBalance(leaveReqs, u.email, now, u.leaveEntitlement || ENTITLEMENT_DAYS);

  const activeAll = users.filter((u) => u.active !== false);
  const formerAll = users.filter((u) => u.active === false);
  // The roster still lists a room screen (marked "Room screen") so it can be
  // managed; the counts and figures are people only (people.js, v4.7.2).
  const activePeople = activeAll.filter(isPerson);

  // Org-level numbers for the anchor / brief / figures card (§1.2–1.4). Derived,
  // never restated as literals.
  const avgUtil = activePeople.length ? Math.round(activePeople.reduce((s, u) => s + utilOf(u), 0) / activePeople.length) : 0;
  const onLeaveList = activePeople.filter(onLeaveToday);
  const overCap = activePeople.filter((u) => utilOf(u) >= overload());
  const liveLedTotal = Object.values(liveByLead).reduce((s, n) => s + n, 0);

  // ── State ── The 9c artboard shows a flat A–Z roster with no role-tab/search
  // toolbar, so those filters are dropped. The Former toggle (and every write path)
  // is preserved. The old local Show-financials toggle is retired: sensitive figures
  // are now wrapped in secretFig and masked by the global top-bar Figures toggle.
  let showFormer = false;

  // ── v4.0.0 (design export 22/10/2026, p23; docs/ui-refresh/02 §5) ──────────
  // A page head ("Team" + "9 people · 1 on leave today"), the four-figure ruled
  // strip, a one-line lock strip, then the roster on hairlines. No anchor
  // numeral, no brief card. Charge-out and Cost are dots while pay is locked
  // (never fetched), and through secretFig — the figures shield — once unlocked.
  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const pad2 = (n) => String(n).padStart(2, '0');
  /** The first working day after a leave request ends: "Tue 27/10". */
  const backOn = (u) => {
    const r = leaveOnDay(leaveReqs, u.email, todayIso);
    if (!r) return '';
    const d = new Date(`${r.to || r.from}T12:00:00`);
    if (isNaN(d)) return '';
    do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
    return `${DAY_NAMES[d.getDay()]} ${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
  };
  const showSig = !!sigCfg;
  const showMoney = isSenior;
  // PERSON 30 · LEADS 4 · UTILISATION 28 · LEAVE LEFT 7 · SIGNATURE 10 · CHARGE-OUT 9 · COST 7 · ··· 3
  const tail = [].concat(showMoney ? ['minmax(72px,.9fr)', 'minmax(64px,.7fr)'] : [], editPatterns ? ['28px'] : []);
  const COLS = ['minmax(150px,3fr)', 'minmax(40px,.45fr)', 'minmax(0,2.8fr)', 'minmax(64px,.75fr)']
    .concat(showSig ? ['minmax(96px,1fr)'] : [], tail).join(' ');
  // iPad: Leads, Leave left and Signature fold away (they live on the person page).
  const COLS_NARROW = ['minmax(150px,3fr)', 'minmax(0,2fr)'].concat(tail).join(' ');
  const avatar = (name, off) => `<span class="av tm4-av${off ? ' off' : ''}" aria-hidden="true">${escapeHtml(initials(name))}</span>`;
  const menuCell = (u) => `<span class="tm4-menu"><button type="button" class="tm9-dot" data-id="${escapeHtml(u.id)}" aria-label="More for ${escapeHtml(titleCase(u.displayName || u.id))}">&#8943;</button></span>`;
  // While locked the rates are not fetched at all, so there is nothing to mask:
  // dots only. Once unlocked, the global figures shield decides.
  const locked = isSenior && !payUnlocked;
  const costCell = (id) => (locked ? secretFig('', false) : secretFig(rates[id] == null ? '—' : `£${Number(rates[id])}`, isSenior));
  const chargeCell = (u) => (locked ? secretFig('', false) : secretFig(u.chargeOutRate ? `£${Number(u.chargeOutRate)}/hr` : '—', isSenior));
  const roleLine = (u) => String(u.jobTitle || '').trim() || (u.roles || []).join(' · ') || '—';

  const headHtml = `<div class="tm4-row tm4-th" role="row">
    <span>Person</span><span class="r tm4-c-x">Leads</span><span>Utilisation</span><span class="r tm4-c-x">Leave left</span>${showSig ? '<span class="tm4-c-x">Signature</span>' : ''}${showMoney ? '<span class="c">Charge-out</span><span class="c">Cost</span>' : ''}${editPatterns ? '<span></span>' : ''}</div>`;

  const target = overload();
  const utilBar = (util) => {
    const tone = util >= target ? 'over' : util >= 55 ? 'on' : 'low';
    return `<span class="tm4-util ${tone}"><span class="tm4-track"><span class="tm4-fill draws" style="width:${Math.min(100, Math.max(0, util))}%"></span><span class="tm4-tick" style="left:${Math.min(100, target)}%"></span></span><span class="tm4-pct">${util}%</span></span>`;
  };

  const activeRowHtml = (u) => {
    const util = utilOf(u); const lb = leaveLeft(u);
    const name = titleCase(u.displayName || u.id);
    const sub = roleLine(u) + (isRoomScreen(u) ? ' · Room screen' : '') + (onLeaveToday(u) ? ' · on leave' : '');
    let cells = `<span class="tm4-person">${avatar(name)}<span class="tm4-who"><span class="tm4-name">${escapeHtml(name)}</span><span class="tm4-sub">${escapeHtml(sub)}</span></span></span>
      <span class="r tm4-num tm4-c-x">${escapeHtml(String(liveByLead[u.id] || 0))}</span>
      ${utilBar(util)}
      <span class="r tm4-leave tm4-c-x">${escapeHtml(lb.remaining + ' of ' + lb.entitlement)}</span>`;
    if (showSig) {
      const st = signatureState(u);
      cells += `<span class="tm4-sig tm4-c-x ${st.cls}">${escapeHtml(st.label)}</span>`;
    }
    if (showMoney) cells += `<span class="c tm4-money">${chargeCell(u)}</span><span class="c tm4-money">${costCell(u.id)}</span>`;
    if (editPatterns) cells += menuCell(u);
    return `<div class="tm4-row tm9-row" role="row" data-id="${escapeHtml(u.id)}">${cells}</div>`;
  };

  const formerRowHtml = (u) => {
    const name = titleCase(u.displayName || u.id);
    let cells = `<span class="tm4-person">${avatar(name, true)}<span class="tm4-who"><span class="tm4-name">${escapeHtml(name)}</span><span class="tm4-sub">${escapeHtml(roleLine(u))} · former</span></span></span>
      <span class="r muted tm4-c-x">—</span><span class="muted">—</span><span class="r muted tm4-c-x">—</span>${showSig ? '<span class="muted tm4-c-x">—</span>' : ''}`;
    if (showMoney) cells += `<span class="c tm4-money">${chargeCell(u)}</span><span class="c tm4-money">${costCell(u.id)}</span>`;
    if (editPatterns) cells += menuCell(u);
    return `<div class="tm4-row tm9-row former" role="row" data-id="${escapeHtml(u.id)}">${cells}</div>`;
  };

  const footNote = `<p class="tm4-foot">Charge-out feeds billed value; cost is the break-even hourly rate and feeds a project&#8217;s true cost. Access needs ${escapeHtml(accessNeeds())} and an active entry &#8212; leavers are marked inactive, never deleted.${showSig ? ' Signatures deploy through Gmail from the template in Files &#8250; Practice &#8250; Signature.' : ''}</p>`;

  const formerLineHtml = () => `<button type="button" class="tm9-former-line linkbtn">${showFormer ? 'Hide former team' : `Show former team (${formerAll.length})`}</button>`;

  // A small transient note anchored under an element — used by the row-menu items
  // that are not built yet. No toast, no persistence; the note fades itself.
  const flashNote = (anchorEl, text) => {
    const n = document.createElement('div');
    n.textContent = text;
    Object.assign(n.style, {
      position: 'fixed', background: 'var(--brand-navy)', color: 'var(--brand-white)', fontSize: '12px',
      lineHeight: '1.4', padding: '9px 13px', borderRadius: '8px', maxWidth: '260px',
      boxShadow: '0 12px 32px rgba(12,17,57,0.14)', zIndex: '500',
    });
    document.body.appendChild(n);
    const r = anchorEl.getBoundingClientRect();
    n.style.top = `${Math.round(r.bottom + 6)}px`;
    n.style.left = `${Math.max(8, Math.round(r.right - 240))}px`;
    setTimeout(() => { n.style.transition = 'opacity 200ms var(--ease-out)'; n.style.opacity = '0'; setTimeout(() => n.remove(), 220); }, 2400);
  };

  // Busiest first (the design's order): the people to protect sit at the top.
  const byUtil = (a, b) => (utilOf(b) - utilOf(a)) || byName(a, b);
  const draw = () => {
    const rows = activeAll.slice().sort(byUtil).map(activeRowHtml).join('');
    const formers = showFormer ? formerAll.slice().sort(byName).map(formerRowHtml).join('') : '';
    mount.querySelector('#tm-rows').innerHTML = headHtml + `<div class="arrive">${rows}${formers}</div>`;
    mount.querySelector('#tm-former').innerHTML = (formerAll.length ? formerLineHtml() : '') + footNote;

    // Row → person detail (unchanged flow).
    const byId = new Map(users.map((u) => [u.id, u]));
    mount.querySelectorAll('#tm-rows .tm9-row').forEach((r) => r.addEventListener('click', () => {
      const u = byId.get(r.dataset.id); if (u) renderMember(u);
    }));

    // 6c Team row menu — one component (openRowMenu), the Team item set. Max 6,
    // first item opens the record, the ending action last. Only when the pilot
    // gate is on.
    mount.querySelectorAll('#tm-rows .tm9-dot').forEach((dot) => dot.addEventListener('click', (e) => {
      e.stopPropagation(); // do NOT also open the member (the row's own click handler)
      const u = byId.get(dot.dataset.id); if (!u) return;
      const isOwner = session.roles.includes('Owner'); // §6: permission is Owner-only
      openRowMenu(dot, [
        { label: 'Open person', onClick: () => renderMember(u) },              // first item always opens the record
        { label: 'Edit details', onClick: () => openEdit(u) },                 // → existing openEdit overlay
        { label: 'Log time against…', onClick: () => flashNote(dot, 'Opens Time filtered to this person — build item') }, // TODO: no Time-filtered-to-person route yet (Time module)
        { label: 'Change permission', role: 'Owner', allowed: isOwner,         // greyed unless Owner; the enum-pill flow (§7 item 6) lives on the record
          onClick: () => renderMember(u) },                                    // no inline menu flow — open the record where the permission pill is
        { label: 'Mark as former…', ending: true,                              // 6g ending confirm; offboarding is senior-gated (§6)
          role: canOffboard ? undefined : 'Owner · Director', allowed: canOffboard, onClick: () => markAsFormer(u) },
      ]);
    }));

    const fl = mount.querySelector('.tm9-former-line');
    if (fl) fl.addEventListener('click', () => { showFormer = !showFormer; draw(); });
  };

  // ── The figures ────────────────────────────────────────────────────────────
  const sigTotal = activeAll.length;
  const sigCurrent = showSig ? activeAll.filter((u) => /^Current/.test(signatureState(u).label)).length : 0;
  const sigOutdated = showSig ? activeAll.filter((u) => /^Outdated/.test(signatureState(u).label)).length : 0;
  const sigUnset = showSig ? activeAll.filter((u) => signatureState(u).label === 'Not set').length : 0;
  const sigFailed = showSig ? activeAll.filter((u) => signatureState(u).label === 'Failed').length : 0;
  const sigSub = [sigOutdated ? `${sigOutdated} outdated` : null, sigUnset ? `${sigUnset} not set` : null,
    sigFailed ? `${sigFailed} failed` : null].filter(Boolean).join(' · ') || `all on v${sigVersion || 1}`;
  const overSub = overCap.length
    ? overCap.slice().sort(byUtil).map((u) => `${firstName(u.displayName || u.id)} ${utilOf(u)}%`).join(', ')
    : 'nobody over';
  const leaveSub = onLeaveList.length
    ? onLeaveList.map((u) => { const b = backOn(u); return `${firstName(u.displayName || u.id)}${b ? `, back ${b}` : ''}`; }).join('; ')
    : 'everyone in';
  const leaveAgg = activePeople.reduce((a, u) => { const lb = leaveLeft(u); a.rem += lb.remaining; a.ent += lb.entitlement; return a; }, { rem: 0, ent: 0 });
  const fig = (label, value, sub, late) => `<div class="fig"><div class="fig-label">${escapeHtml(label)}</div><div class="fig-value${late ? ' late' : ''}">${escapeHtml(value)}</div><div class="fig-note">${escapeHtml(sub)}</div></div>`;

  const LOCK_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';
  const lockHtml = !isSenior ? '' : locked
    ? `<div class="tm4-lock">${LOCK_SVG}<span>Pay, cost rates and employment records are locked</span><button type="button" class="btn" data-pay-unlock>Unlock with Face ID or PIN</button></div>`
    : `<div class="tm4-lock open">${LOCK_SVG}<span>Pay, cost rates and employment records are unlocked for a few minutes</span></div>`;
  // The person page shows the same strip while locked.
  const lockBarHtml = locked ? lockHtml : '';

  const headHtmlTop = `<div class="tm4-head">
      <div class="page-h"><h1>Team</h1><span class="page-sub">${escapeHtml(`${activePeople.length} ${activePeople.length === 1 ? 'person' : 'people'} · ${onLeaveList.length} on leave today`)}</span></div>
      <div class="tm4-actions">
        ${showSig && (sigOutdated || sigUnset) ? '<button type="button" id="tm-deploy-sig" class="btn">Deploy signatures</button>' : ''}
        ${isSenior && !onAspHost() ? '<button type="button" id="tm-invite" class="btn">Invite</button>' : ''}
        ${isSenior ? '<button type="button" id="tm-add" class="btn primary">Add someone</button>' : ''}
      </div>
    </div>`;
  if (headEl) headEl.innerHTML = headHtmlTop;

  mount.querySelector('#tm-loading')?.remove();
  mount.innerHTML = `<div class="tm4">
      ${headEl ? '' : headHtmlTop}
      <div class="figs tm4-figs">
        ${fig('Utilisation, last 4 weeks', `${avgUtil}%`, `target ${target}%`)}
        ${fig('Over target', String(overCap.length), overSub, overCap.length > 0)}
        ${fig('On leave today', String(onLeaveList.length), leaveSub)}
        ${showSig
          ? fig('Signatures current', `${sigCurrent} of ${sigTotal}`, sigSub)
          : fig('Leave left, all in', String(leaveAgg.rem), `of ${leaveAgg.ent} days`)}
      </div>
      ${lockHtml}
      <div id="tm-rows" class="tm4-table" role="table" style="--tm-cols:${COLS};--tm-cols-narrow:${COLS_NARROW}"></div>
      <div id="tm-former" class="tm4-after"></div>
    </div>`;

  const root = headEl || mount;
  const addBtn = root.querySelector('#tm-add'); if (addBtn) addBtn.addEventListener('click', openAddPerson);
  const invBtn = root.querySelector('#tm-invite'); if (invBtn) invBtn.addEventListener('click', openInvite);
  const deployBtn = root.querySelector('#tm-deploy-sig');
  if (deployBtn) deployBtn.addEventListener('click', () => openSignatureTab());

  // ── Edit person — slide-over panel (edit-patterns §2.04, artboard 6d) ─────────
  // Replaces the old centred openOverlay modal. Pattern 04 "edit panel": several
  // fields commit together as ONE Save event. 520px from the right, same geometry
  // as the Golden thread slide-over ("one panel in the app"), behind a
  // rgba(12,17,57,0.42) scrim. Self-contained: built into document.body, animated
  // in per the motion table (200ms), and torn down on ✕ / Cancel / scrim / Escape.
  // Every write path (updateUserFields, setFinanceRate) is carried over; each
  // changed field also writes ONE change-list line via addRecordChange.
  // Access role vocabulary (§ Fields item 2). NOTE (§8 open, flag for Karan): ASP
  // conflates JOB TITLE and ACCESS ROLE in `u.roles` — a person's roles[0] can be
  // "Senior Structural Engineer" (a title), not one of these access roles. This
  // field edits the PRIMARY role; when roles[0] isn't in the vocab we prepend it
  // as an option so opening the panel never silently rewrites it.
  const EDIT_ROLES = ['Owner', 'Director', 'Finance', 'Coordinator', 'Engineer', 'Technician'];

  // ── Timesheets: Required | Not required (Karan 26/10/2026) ───────────────
  // users.timesheetExempt, written only by the setTimesheetRequired callable
  // (Owner or Director; the rules give the field to nobody). Everyone else sees
  // the value as words, never a disabled control.
  const canSetTimesheets = session.roles.some((r) => r === 'Owner' || r === 'Director');
  function timesheetToggleHtml(u) {
    const notReq = isTimesheetExempt(u);
    if (!canSetTimesheets) return `<span class="bm-body">${notReq ? 'Not required' : 'Required'}</span>`;
    const b = (val, on, label) => `<button type="button" data-ts="${val}" class="${on ? 'active' : ''}" aria-pressed="${on}">${label}</button>`;
    return `<span class="seg" role="group" aria-label="Timesheets">${b('required', !notReq, 'Required')}${b('not', notReq, 'Not required')}</span>`;
  }
  function wireTimesheetToggle(u) {
    const host = mount.querySelector('#pm-ts');
    const err = mount.querySelector('#pm-ts-err');
    if (!host || !canSetTimesheets) return;
    host.querySelectorAll('button[data-ts]').forEach((btn) => btn.addEventListener('click', async () => {
      const required = btn.dataset.ts === 'required';
      if (required === !isTimesheetExempt(u)) return;
      host.querySelectorAll('button').forEach((x) => { x.disabled = true; });
      if (err) err.hidden = true;
      try {
        await callFunction('setTimesheetRequired', { userId: u.id, required });
        const at = new Date().toISOString();
        await addRecordChange({ record: 'users/' + u.id, field: 'timesheetExempt', label: 'Timesheets', from: required ? 'Not required' : 'Required', to: required ? 'Required' : 'Not required', by: session.email, byName: session.displayName, at, money: false }).catch(() => {});
        u.timesheetExempt = !required;
        invalidateCache('users');
        renderMember(u);
      } catch (e) {
        host.querySelectorAll('button').forEach((x) => { x.disabled = false; });
        if (err) { err.textContent = 'Couldn’t save — ' + ((e && e.message) || e); err.hidden = false; }
      }
    }));
  }

  // ── Room screen: No | Yes (Karan 26/10/2026) ────────────────────────────
  // users.roomScreen, written only by the setRoomScreen callable (Owner or
  // Director; the rules give the field to nobody). The account keeps its
  // permission (Meeting Room stays a Coordinator) but isn't counted as a person
  // (people.js), and it may open the room display (/room).
  const canSetRoomScreen = canSetTimesheets;
  function roomScreenToggleHtml(u) {
    const on = isRoomScreen(u);
    if (!canSetRoomScreen) return `<span class="bm-body">${on ? 'Yes' : 'No'}</span>`;
    const b = (val, sel, label) => `<button type="button" data-rs="${val}" class="${sel ? 'active' : ''}" aria-pressed="${sel}">${label}</button>`;
    return `<span class="seg" role="group" aria-label="Room screen">${b('no', !on, 'No')}${b('yes', on, 'Yes')}</span>`;
  }
  function wireRoomScreenToggle(u) {
    mount.querySelector('#pm-rs-open')?.addEventListener('click', () => { location.href = '/room'; });
    const host = mount.querySelector('#pm-rs');
    const err = mount.querySelector('#pm-rs-err');
    if (!host || !canSetRoomScreen) return;
    host.querySelectorAll('button[data-rs]').forEach((btn) => btn.addEventListener('click', async () => {
      const on = btn.dataset.rs === 'yes';
      if (on === isRoomScreen(u)) return;
      host.querySelectorAll('button').forEach((x) => { x.disabled = true; });
      if (err) err.hidden = true;
      try {
        await callFunction('setRoomScreen', { userId: u.id, on });
        const at = new Date().toISOString();
        await addRecordChange({ record: 'users/' + u.id, field: 'roomScreen', label: 'Room screen', from: on ? 'No' : 'Yes', to: on ? 'Yes' : 'No', by: session.email, byName: session.displayName, at, money: false }).catch(() => {});
        u.roomScreen = on;
        invalidateCache('users');
        renderMember(u);
      } catch (e) {
        host.querySelectorAll('button').forEach((x) => { x.disabled = false; });
        if (err) { err.textContent = 'Couldn’t save — ' + ((e && e.message) || e); err.hidden = false; }
      }
    }));
  }

  function openEdit(u) {
    const origNum = (v) => (v == null || v === '') ? '' : String(Number(v));
    const num = (v) => (v == null || v === '') ? null : Number(v);
    // Total hours already logged by this person (all sheets, incl. imported) — used
    // by the consequence note. Cheap: `sheets` is already in the closure.
    const emailLc = String(u.email || '').toLowerCase();
    const loggedMins = sheets.reduce((sum, s) => {
      if (String(s.userEmail || '').toLowerCase() !== emailLc) return sum;
      (s.rows || []).forEach((r) => Object.values(r.days || {}).forEach((v) => { sum += parseHM(v); }));
      return sum;
    }, 0);
    const loggedHours = Math.round(loggedMins / 60);

    const isSeniorViewer = isSenior;                        // finance-field gate (§6)
    // Job title, post-nominals and mobile (what the email signature says): Owner and
    // Directors, as firestore.rules' signature rule allows (Karan 02/11/2026).
    const sigEditor = (session.roles || []).some((r) => r === 'Owner' || r === 'Director');
    const uSenior = (u.roles || []).some((r) => SENIOR.includes(r)); // the RECORD's derived permission
    const origRole = (u.roles || [])[0] || '';
    const roleOptList = (EDIT_ROLES.includes(origRole) || !origRole) ? EDIT_ROLES : [origRole, ...EDIT_ROLES];

    // Header meta — show what exists, omit gracefully.
    const joinedY = u.joined || u.joinedYear || (u.createdAt ? new Date(u.createdAt).getFullYear() : null);
    const lastEditDate = u.updatedAt ? new Date(u.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
    const editor = u.updatedBy || null;
    const metaParts = [];
    if (joinedY) metaParts.push('Joined ' + joinedY);
    if (lastEditDate) metaParts.push('last edited ' + lastEditDate + (editor ? ' by ' + editor : ''));
    const metaLine = metaParts.join(' · ');

    // Shared token strings (reuse only; no new colours).
    const LABEL = 'font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-3)';
    const BOX = 'display:flex;align-items:center;gap:8px;border:1px solid var(--hair-strong);border-radius:8px;padding:0 13px;background:var(--surface)';
    const INPUT = (mono) => `flex:1;min-width:0;border:0;background:transparent;padding:10px 0;font-size:13.5px;color:inherit${mono ? ';font-family:var(--font-technical)' : ''}`;

    // An editable field column (input inside a bordered .ed-box that takes the halo).
    const inputField = (id, label, val, { mono = false, suffix = '', full = false } = {}) => `
      <div style="display:flex;flex-direction:column;gap:7px${full ? ';grid-column:1 / -1' : ''}">
        <span style="${LABEL}">${escapeHtml(label)}</span>
        <div class="ed-box" style="${BOX}">
          <input id="${id}" type="text" autocomplete="off" value="${escapeHtml(val)}" style="${INPUT(mono)}">
          ${suffix ? `<span style="font-size:13px;color:var(--ink-3);${mono ? 'font-family:var(--font-technical);' : ''}">${escapeHtml(suffix)}</span>` : ''}
        </div>
      </div>`;

    // A role-gated read row (§6): the value stated with the holding role named at the
    // right — the same treatment as the refused inline row, NEVER a disabled input.
    const gatedRow = (label, valueText) => `
      <div style="display:flex;flex-direction:column;gap:7px">
        <span style="${LABEL}">${escapeHtml(label)}</span>
        <div style="border:1px solid var(--hair);border-radius:8px;padding:12px 14px;display:flex;align-items:center;gap:12px;background:var(--brief-bg)">
          <span style="flex:1;font-size:13.5px;color:var(--ink-2);font-family:var(--font-technical)">${escapeHtml(valueText)}</span>
          <span style="font-size:10px;font-weight:500;letter-spacing:0.09em;text-transform:uppercase;color:var(--ink-3)">Owner &#183; Director &#183; Finance</span>
        </div>
      </div>`;

    const rateText = (v) => v == null || v === '' ? '—' : '£' + Number(v).toFixed(2) + ' / hr';
    const salaryText = (v) => v == null || v === '' || Number(v) === 0 ? '—' : GBP(v) + ' / yr';
    const chargeFieldHtml = isSeniorViewer
      ? inputField('ed-charge', 'Charge-out', origNum(u.chargeOutRate), { mono: true })
      : gatedRow('Charge-out', rateText(u.chargeOutRate));
    const costFieldHtml = isSeniorViewer
      ? inputField('ed-cost', 'Break-even cost', origNum(rates[u.id]), { mono: true })
      : gatedRow('Break-even cost', rateText(rates[u.id]));
    // Annual salary — finance-gated like Break-even cost (saved via setUserSalary,
    // bound to salaries[u.id]). Restored to the 6d panel (regression: the rebuilt
    // slide-over dropped the OLD modal's salary field). Seniors edit it; everyone
    // else gets the light-grey read row with the holding role named.
    const salaryFieldHtml = isSeniorViewer
      ? inputField('ed-salary', 'Annual salary (£)', origNum(salaries[u.id]), { mono: true })
      : gatedRow('Annual salary', salaryText(salaries[u.id]));
    // ── Employment record (Karan 26/08): NI number, date of birth, start date,
    // pension opt-in. Personal data — held in hrRecords, never on the org-readable
    // users doc, and gated to the same three roles as the money.
    const hr = isSeniorViewer ? (hrs[u.id] || {}) : {};
    const DOTS = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022';
    const dateField = (id, label, val) => `
      <div style="display:flex;flex-direction:column;gap:7px">
        <span style="${LABEL}">${escapeHtml(label)}</span>
        <div class="ed-box" style="${BOX}">
          <input id="${id}" type="date" value="${escapeHtml(val || '')}" style="${INPUT(true)};cursor:pointer">
        </div>
      </div>`;
    const selectField = (id, label, val, opts) => `
      <div style="display:flex;flex-direction:column;gap:7px">
        <span style="${LABEL}">${escapeHtml(label)}</span>
        <div class="ed-box" style="${BOX}">
          <select id="${id}" style="${INPUT(false)};appearance:none;cursor:pointer">
            ${opts.map((o) => `<option value="${escapeHtml(o)}"${String(val) === o ? ' selected' : ''}>${escapeHtml(o || '—')}</option>`).join('')}
          </select>
        </div>
      </div>`;
    const employmentHtml = isSeniorViewer
      ? `${inputField('ed-ni', 'NI number', hr.niNumber || '', { mono: true })}
         ${selectField('ed-pension', 'Pension', hr.pension || '', ['', 'Opted in', 'Opted out'])}
         ${dateField('ed-dob', 'Date of birth', hr.dateOfBirth)}
         ${dateField('ed-started', 'Start date', hr.startDate)}`
      // Belt and braces: a non-senior never FETCHES hrRecords (the gate is at the
      // data layer above, and again in the rules), so `hr` is empty here — but
      // don't render from it regardless. The mask must not depend on the fetch
      // having come back blank.
      : `${gatedRow('NI number', DOTS)}
         ${gatedRow('Date of birth', DOTS)}
         ${gatedRow('Start date', DOTS)}
         ${gatedRow('Pension', DOTS)}`;

    // Avatar colour — the ASP-palette swatch row the OLD modal had, restored to the
    // top of the panel. iconColor is the person's current swatch (may be empty).
    const origColor = u.iconColor || '';
    let selectedColor = origColor;
    const swatchesHtml = `<div style="display:flex;flex-direction:column;gap:9px">
      <span style="${LABEL}">Avatar colour</span>
      <div id="ed-swatches" style="display:flex;gap:11px">
        ${ASP_COLOURS.map((c) => `<span class="ed-sw" data-color="${escapeHtml(c)}" style="width:26px;height:26px;flex:none;border-radius:50%;background:${escapeHtml(c)};cursor:pointer"></span>`).join('')}
      </div>
    </div>`;

    // Scrim + panel appended to a wrapper on document.body (above the app + row menu).
    const wrap = document.createElement('div');
    const scrim = document.createElement('div');
    scrim.setAttribute('style', 'position:fixed;inset:0;background:rgba(12,17,57,0.42);z-index:600;opacity:0;transition:opacity 200ms var(--ease-out)');
    const panel = document.createElement('div');
    panel.setAttribute('style', 'position:fixed;top:0;right:0;bottom:0;width:520px;max-width:calc(100 * var(--vw));background:var(--surface);border-left:1px solid var(--hair);box-shadow:-18px 0 48px rgba(12,17,57,0.18);display:flex;flex-direction:column;z-index:601;transform:translateX(100%);transition:transform 200ms var(--ease-out)');
    panel.innerHTML = `
      <div style="padding:26px 30px 20px;border-bottom:1px solid var(--hair);display:flex;align-items:flex-start;gap:14px">
        <div style="flex:1;display:flex;flex-direction:column;gap:5px">
          <span style="${LABEL};color:var(--brand-orange)">Edit person</span>
          <span style="font-size:24px;font-weight:500;letter-spacing:-0.025em">${escapeHtml(titleCase(u.displayName || u.id))}</span>
          ${metaLine ? `<span style="font-size:12.5px;color:var(--ink-3)">${escapeHtml(metaLine)}</span>` : ''}
        </div>
        <span id="ed-close" style="font-size:19px;color:var(--ink-3);line-height:1;cursor:pointer">&#10005;</span>
      </div>

      <div style="flex:1;padding:24px 30px;display:flex;flex-direction:column;gap:22px;overflow-y:auto">
        ${swatchesHtml}
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          ${inputField('ed-name', 'Full name', u.displayName || '')}
          <div style="display:flex;flex-direction:column;gap:7px">
            <span style="${LABEL}">Role</span>
            <div class="ed-box" style="${BOX}">
              <select id="ed-role" style="${INPUT(false)};cursor:pointer">${roleOptList.map((r) => `<option${r === origRole ? ' selected' : ''}>${escapeHtml(r)}</option>`).join('')}</select>
            </div>
          </div>
        </div>

        ${inputField('ed-email', 'Email', u.email || '', { full: true })}

        ${sigEditor ? `<div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          ${inputField('ed-jobtitle', 'Job title', u.jobTitle || '', { full: true })}
          ${inputField('ed-prenom', 'Pre-nominals', u.preNominals || '')}
          ${inputField('ed-postnom', 'Post-nominals', u.postNominals || '')}
          ${inputField('ed-mobile', 'Mobile', u.mobile || '')}
        </div>` : ''}

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          ${chargeFieldHtml}
          ${costFieldHtml}
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          ${salaryFieldHtml}
          <div></div>
        </div>

        <div style="display:flex;align-items:center;gap:12px;margin-top:4px">
          <span style="font-size:10px;font-weight:500;letter-spacing:0.09em;text-transform:uppercase;color:var(--ink-3)">Employment</span>
          <span style="flex:1;height:1px;background:var(--hair)"></span>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          ${employmentHtml}
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          ${inputField('ed-leave', 'Leave allowance', origNum(u.leaveEntitlement), { mono: true, suffix: 'days' })}
          ${inputField('ed-overload', 'Overload at', (u.overloadAt != null ? String(Number(u.overloadAt)) : String(overload())), { mono: true, suffix: '%' })}
        </div>

        <div style="display:flex;flex-direction:column;gap:8px;padding-top:4px">
          <span style="${LABEL}">Permission</span>
          <!-- Read row: permission is the enum-pill flow (§7 build item 6), not an input here. -->
          <div style="border:1px solid var(--hair);border-radius:8px;padding:12px 14px;display:flex;align-items:center;gap:12px;background:var(--brief-bg)">
            <span style="flex:1;font-size:13.5px;color:var(--ink-2)">${uSenior ? 'Full financials' : 'Team &#8212; no finance figures'}</span>
            <span style="font-size:10px;font-weight:500;letter-spacing:0.09em;text-transform:uppercase;color:var(--ink-3)">Owner only</span>
          </div>
        </div>

        <div id="ed-consequence" style="margin-top:auto;padding:16px 18px;background:var(--nav-hover);border-radius:12px;display:none;gap:12px;align-items:flex-start">
          <span style="width:9px;height:9px;flex:none;border-radius:50%;background:var(--brand-blue);margin-top:4px;box-shadow:0 0 0 3px var(--halo)"></span>
          <span id="ed-consequence-text" style="font-size:12.5px;line-height:1.55;color:var(--anchor)"></span>
        </div>
      </div>

      <div id="ed-error" style="display:none;padding:0 30px 8px;color:var(--status-error);font-size:12.5px"></div>

      <div style="padding:18px 30px 22px;border-top:1px solid var(--hair);display:flex;align-items:center;gap:12px">
        <span id="ed-save" style="height:40px;display:inline-flex;align-items:center;padding:0 22px;border-radius:8px;background:var(--brand-blue);color:var(--brand-white);font-size:13.5px;font-weight:500;cursor:pointer">Save changes</span>
        <span id="ed-cancel" style="height:40px;display:inline-flex;align-items:center;padding:0 18px;border-radius:8px;font-size:13.5px;color:var(--ink-2);cursor:pointer">Cancel</span>
        <span style="flex:1"></span>
        <span id="ed-count" style="font-size:12px;color:var(--ink-3)">0 changes</span>
      </div>`;
    wrap.appendChild(scrim);
    wrap.appendChild(panel);
    document.body.appendChild(wrap);
    requestAnimationFrame(() => { scrim.style.opacity = '1'; panel.style.transform = 'translateX(0)'; });

    let closed = false;
    const close = () => {
      if (closed) return; closed = true;
      document.removeEventListener('keydown', onKey);
      scrim.style.opacity = '0'; panel.style.transform = 'translateX(100%)';
      setTimeout(() => wrap.remove(), 200);
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } };
    document.addEventListener('keydown', onKey);
    scrim.addEventListener('click', close);
    panel.querySelector('#ed-close').addEventListener('click', close);
    panel.querySelector('#ed-cancel').addEventListener('click', close);

    // ── Change tracking ────────────────────────────────────────────────────────
    // Each field: its DOM id, kind (text|num), original value, the change-list
    // `from`, label, and whether it is money. Gated read rows (charge/cost when the
    // viewer isn't senior) have no input → querySelector guards skip them.
    const fieldDefs = [
      { key: 'displayName', id: 'ed-name', kind: 'text', label: 'Full name', orig: (u.displayName || ''), from: (u.displayName || '') },
      { key: 'role', id: 'ed-role', kind: 'text', label: 'Role', orig: origRole, from: origRole },
      { key: 'email', id: 'ed-email', kind: 'text', label: 'Email', orig: (u.email || ''), from: (u.email || '') },
      // `sig: true` goes in its own write: the rules allow these only on their own.
      { key: 'jobTitle', id: 'ed-jobtitle', kind: 'text', label: 'Job title', sig: true, orig: (u.jobTitle || ''), from: (u.jobTitle || '') },
      { key: 'preNominals', id: 'ed-prenom', kind: 'text', label: 'Pre-nominals', sig: true, orig: (u.preNominals || ''), from: (u.preNominals || '') },
      { key: 'postNominals', id: 'ed-postnom', kind: 'text', label: 'Post-nominals', sig: true, orig: (u.postNominals || ''), from: (u.postNominals || '') },
      { key: 'mobile', id: 'ed-mobile', kind: 'text', label: 'Mobile', sig: true, orig: (u.mobile || ''), from: (u.mobile || '') },
      { key: 'chargeOutRate', id: 'ed-charge', kind: 'num', label: 'Charge-out', money: true, orig: origNum(u.chargeOutRate), from: num(u.chargeOutRate) },
      { key: 'breakEven', id: 'ed-cost', kind: 'num', label: 'Break-even cost', money: true, orig: origNum(rates[u.id]), from: num(rates[u.id]) },
      { key: 'salary', id: 'ed-salary', kind: 'num', label: 'Annual salary', money: true, orig: origNum(salaries[u.id]), from: num(salaries[u.id]) },
      { key: 'leaveEntitlement', id: 'ed-leave', kind: 'num', label: 'Leave allowance', orig: origNum(u.leaveEntitlement), from: num(u.leaveEntitlement) },
      { key: 'overloadAt', id: 'ed-overload', kind: 'num', label: 'Overload at', orig: (u.overloadAt != null ? String(Number(u.overloadAt)) : String(overload())), from: (u.overloadAt != null ? Number(u.overloadAt) : overload()) },
      // Employment record. `hr: true` routes the write to setHrRecord, and
      // `private: true` keeps it out of the org-readable change list. All four
      // carry it: Karan called the whole employment record sensitive, and
      // whether a colleague opted out of the pension is nobody else's business.
      { key: 'niNumber', id: 'ed-ni', kind: 'text', label: 'NI number', hr: true, private: true, orig: (hr.niNumber || ''), from: (hr.niNumber || '') },
      { key: 'dateOfBirth', id: 'ed-dob', kind: 'text', label: 'Date of birth', hr: true, private: true, orig: (hr.dateOfBirth || ''), from: (hr.dateOfBirth || '') },
      { key: 'startDate', id: 'ed-started', kind: 'text', label: 'Start date', hr: true, private: true, orig: (hr.startDate || ''), from: (hr.startDate || '') },
      { key: 'pension', id: 'ed-pension', kind: 'text', label: 'Pension', hr: true, private: true, orig: (hr.pension || ''), from: (hr.pension || '') },
    ];
    const normalize = (kind, s) => {
      if (kind === 'text') return String(s).trim();
      const t = String(s).trim(); if (t === '') return '';
      const n = Number(t); return isNaN(n) ? t : String(n);
    };
    const elOf = (def) => panel.querySelector('#' + def.id);
    const isChanged = (def) => { const el = elOf(def); return !!el && normalize(def.kind, el.value) !== normalize(def.kind, def.orig); };
    const toValue = (def) => { const el = elOf(def); if (def.kind === 'text') return el.value.trim(); const t = el.value.trim(); return t === '' ? null : Number(t); };
    const chargeDef = fieldDefs.find((d) => d.key === 'chargeOutRate');

    const saveBtn = panel.querySelector('#ed-save');
    const countEl = panel.querySelector('#ed-count');
    const consBox = panel.querySelector('#ed-consequence');
    const consText = panel.querySelector('#ed-consequence-text');
    const errBox = panel.querySelector('#ed-error');
    const setSaveEnabled = (on) => { saveBtn.style.opacity = on ? '1' : '0.45'; saveBtn.style.pointerEvents = on ? 'auto' : 'none'; saveBtn.style.cursor = on ? 'pointer' : 'default'; };

    const recompute = () => {
      let count = 0;
      for (const def of fieldDefs) {
        const el = elOf(def); if (!el) continue;
        const box = el.closest('.ed-box');
        const changed = isChanged(def);
        if (changed) count++;
        // Changed-field halo (6d Charge-out, line 376): blue border + blue-100 ring.
        if (box) {
          box.style.borderColor = changed ? 'var(--brand-blue)' : 'var(--hair-strong)';
          box.style.boxShadow = changed ? '0 0 0 3px var(--halo)' : 'none';
        }
      }
      // Avatar colour is not an input — fold its changed state into the count/Save.
      if (selectedColor !== origColor) count++;
      countEl.textContent = count + ' change' + (count === 1 ? '' : 's');
      setSaveEnabled(count > 0);
      // Consequence note (6d 403-406): only when charge-out is changed.
      const chEl = elOf(chargeDef);
      if (chEl && isChanged(chargeDef)) {
        const from = chargeDef.from, to = toValue(chargeDef);
        const money = (n) => n == null ? '—' : '£' + Number(n).toFixed(2);
        const logged = loggedHours > 0 ? `The ${loggedHours} hours already logged` : 'Hours already logged';
        consText.textContent = `Changing charge-out from ${money(from)} to ${money(to)} affects new time entries only. ${logged} keep the rate they were logged at.`;
        consBox.style.display = 'flex';
      } else {
        consBox.style.display = 'none';
      }
    };
    fieldDefs.forEach((def) => { const el = elOf(def); if (el) { el.addEventListener('input', recompute); el.addEventListener('change', recompute); } });

    // Avatar-colour swatches: paint the selected ring, and on click re-select +
    // re-run the changed-field count (single-Save flow).
    const paintSwatches = () => {
      panel.querySelectorAll('.ed-sw').forEach((sw) => {
        const on = sw.dataset.color === selectedColor;
        sw.style.boxShadow = on ? '0 0 0 2px var(--brand-white), 0 0 0 4px var(--anchor)' : 'none';
      });
    };
    panel.querySelectorAll('.ed-sw').forEach((sw) => sw.addEventListener('click', () => {
      selectedColor = sw.dataset.color; paintSwatches(); recompute();
    }));
    paintSwatches();
    setSaveEnabled(false);

    saveBtn.addEventListener('click', async () => {
      const changedDefs = fieldDefs.filter(isChanged);
      const avatarChanged = selectedColor !== origColor;
      if (!changedDefs.length && !avatarChanged) return;
      setSaveEnabled(false); saveBtn.textContent = 'Saving…'; errBox.style.display = 'none';
      const at = new Date().toISOString();
      const userFields = {};
      const lines = [];
      let beVal, salVal;
      const hrFields = {};
      const sigFields = {};
      for (const def of changedDefs) {
        const to = toValue(def);
        if (def.key === 'breakEven') { beVal = to; }
        else if (def.key === 'salary') { salVal = to; }
        else if (def.hr) { hrFields[def.key] = to; }
        else if (def.sig) { sigFields[def.key] = to || null; if (def.key === 'mobile') sigFields.mobileE164 = (to && toE164(to)) || null; }
        else if (def.key === 'role') { userFields.roles = [to].concat((u.roles || []).slice(1)); }
        else { userFields[def.key] = to; }
        // Per-field change-list line. §2.04-vs-§5 TENSION (flag for Karan): the panel
        // spec says the multi-field commit is "one event / one change-list line", but
        // the 6h change list shows PER-FIELD lines. Per-field is implemented so each
        // change stays individually legible (old→new, money flag) on the record.
        lines.push({ field: def.key, label: def.label, from: def.from, to,
          money: !!def.money, private: !!def.money || !!def.private });
      }
      // Avatar colour — non-money field, written on the org-readable users doc.
      if (avatarChanged) {
        userFields.iconColor = selectedColor;
        lines.push({ field: 'iconColor', label: 'Avatar colour', from: origColor || null, to: selectedColor, money: false });
      }
      try {
        if (Object.keys(userFields).length) {
          userFields.updatedBy = session.email; userFields.updatedAt = at;
          await updateUserFields(u.id, userFields);
          // A role or an offboarding changes what somebody may do, so the claim
          // is rewritten with the record rather than fifteen minutes later.
          if ('roles' in userFields || 'active' in userFields) await announceAccess(u.id, u.email);
        }
        if (Object.keys(sigFields).length) await updateUserFields(u.id, { ...sigFields, updatedBy: session.email, updatedAt: at });
        if (beVal !== undefined) await setFinanceRate(u.id, beVal || 0, session.email);
        if (salVal !== undefined) await setUserSalary(u.id, salVal || 0, session.email);
        if (Object.keys(hrFields).length) await setHrRecord(u.id, hrFields, session.email);
        for (const ln of lines) {
          // A private line — salary, break-even, charge-out, NI, date of birth —
          // must NOT go to `changes`: that collection is org-readable, so logging
          // a pay rise there published it to the whole company. Money goes to the
          // Finance-gated, append-only financeLog with its figures; NI and date of
          // birth are recorded as having changed, without repeating the value.
          if (ln.private) {
            await addFinanceLogEntry(u.id, {
              field: ln.field, label: ln.label,
              from: ln.money ? ln.from : null, to: ln.money ? ln.to : null,
              valueWithheld: !ln.money,
              by: session.email, byName: session.displayName, at,
            });
            continue;
          }
          await addRecordChange({ record: 'users/' + u.id, field: ln.field, label: ln.label, from: ln.from, to: ln.to, by: session.email, byName: session.displayName, at, money: ln.money });
        }
        // Reflect the writes on the in-memory record so the re-render shows them.
        if ('displayName' in userFields) u.displayName = userFields.displayName;
        if ('roles' in userFields) u.roles = userFields.roles;
        if ('email' in userFields) u.email = userFields.email;
        if ('chargeOutRate' in userFields) u.chargeOutRate = userFields.chargeOutRate;
        if ('leaveEntitlement' in userFields) u.leaveEntitlement = userFields.leaveEntitlement;
        if ('overloadAt' in userFields) u.overloadAt = userFields.overloadAt;
        if ('iconColor' in userFields) u.iconColor = userFields.iconColor;
        Object.assign(u, sigFields);
        if (beVal !== undefined) rates[u.id] = beVal || 0;
        if (salVal !== undefined) salaries[u.id] = salVal || 0;
        u.updatedBy = session.email; u.updatedAt = at;
        close();
        renderMember(u); // re-render the record so the new change-list lines show
      } catch (e) {
        saveBtn.textContent = 'Save changes'; setSaveEnabled(true);
        errBox.textContent = 'Couldn’t save — ' + (e && e.message || e); errBox.style.display = 'block';
      }
    });
  }

  // ── Mark as former — the 6g ending confirm (§7 build item 7) ─────────────────
  // One path for both the ⋯ row-menu item and the person-record offboard button
  // (the OLD inline confirm()/pm-off flow is replaced). Nothing is deleted: the
  // person is hidden + sign-in revoked, and the confirm states what SURVIVES first.
  // Senior-gated at the call sites (offboarding is Owner · Director · Finance).
  async function markAsFormer(u) {
    // Assets mission 4: what to collect, read before the confirm opens (read-only; [] without Assets).
    const toCollect = collectLines(await loadKit(u.id));
    const name = titleCase(u.displayName || u.id);
    const emailLc = String(u.email || '').toLowerCase();
    const mins = sheets.reduce((sum, s) => {
      if (String(s.userEmail || '').toLowerCase() !== emailLc) return sum;
      (s.rows || []).forEach((r) => Object.values(r.days || {}).forEach((v) => { sum += parseHM(v); }));
      return sum;
    }, 0);
    const hoursLogged = Math.round(mins / 60);
    const liveLeadCount = projects.filter((p) => ACTIVE_STATUSES.includes(p.status) && refTail(p.lead) === u.id).length;
    openEndingConfirm({
      eyebrow: 'Mark as former',
      record: name,
      whatStays: [
        `${hoursLogged} hours logged, at the rates they were logged at`,
        'Every document, calculation and email already filed',
        `Their name on the ${liveLeadCount} project${liveLeadCount === 1 ? '' : 's'} they lead, until reassigned`,
      ],
      whatChanges: 'Hidden from the table unless Former team is on. Cannot be assigned new work. Sign-in is revoked and they are signed out on every device within the hour.'
        + (toCollect.length ? ` To collect before they go: ${toCollect.map((l) => l.replace(/^Collect /, '')).join('; ')}. Their kit stays assigned to them until it is returned on the asset.` : ''),
      field: { label: 'Last day', type: 'date' },
      confirmLabel: 'Mark as former',
      reversibleNote: 'Reversible',
      onConfirm: async (lastDay) => {
        const at = new Date().toISOString();
        const day = String(lastDay || '').trim();
        // Two writes, because the rules give `active` (Owner/Director) and
        // `lastDay` to different rule branches and one write carrying both
        // matches neither. `active` first: it is the one that matters — the
        // offboardOnInactive trigger takes the sign-in claim away and signs
        // them out — and if it is refused nothing has been written.
        await updateUserFields(u.id, { active: false, updatedBy: session.email, updatedAt: at });
        await updateUserFields(u.id, { lastDay: day || null, updatedBy: session.email, updatedAt: at });
        await addRecordChange({ record: 'users/' + u.id, field: 'status', label: 'Status', from: 'Active', to: 'Former', reason: day ? ('last day ' + day) : null, by: session.email, byName: session.displayName, at, money: false });
        u.active = false; u.lastDay = day || null; u.updatedBy = session.email; u.updatedAt = at;
        renderRoster(mount, session, { onOpenLead, onOpenProject, editPatterns, headEl });
      },
    });
  }

  // ── Make active again (Karan 23/10/2026) — the reverse of Mark as former.
  // offboardOnInactive restores the sign-in Track removed (only an account this
  // company closed); `active` and `lastDay` sit in different rule branches, so
  // two writes, active first.
  function makeActiveAgain(u) {
    const name = titleCase(u.displayName || u.id);
    openEndingConfirm({
      eyebrow: 'Make active again',
      record: name,
      whatStays: ['Their hours, documents and projects, exactly as they were'],
      whatChanges: 'Back in the team table and can be given work again. Their sign-in is restored — they may need to sign out and in once.',
      confirmLabel: 'Make active again',
      reversibleNote: 'Reversible',
      onConfirm: async () => {
        const at = new Date().toISOString();
        await updateUserFields(u.id, { active: true, updatedBy: session.email, updatedAt: at });
        await updateUserFields(u.id, { lastDay: null, updatedBy: session.email, updatedAt: at });
        await addRecordChange({ record: 'users/' + u.id, field: 'status', label: 'Status', from: 'Former', to: 'Active', reason: null, by: session.email, byName: session.displayName, at, money: false });
        u.active = true; u.lastDay = null; u.updatedBy = session.email; u.updatedAt = at;
        renderRoster(mount, session, { onOpenLead, onOpenProject, editPatterns, headEl });
      },
    });
  }

  // ── Add-person dialog (seniors) — unchanged data paths ──
  // Add person — rebuilt on the canonical dialog shell (Dialogs & Forms 7b, build
  // item 4). Creating a record, so it is a 520 modal (7a surface test). Fields
  // ordered per 7g: identity (name, email) → classification (role) → numbers.
  function openAddPerson() {
    // A contractor company adds people by invite (team-reviewers → invite.js),
    // never through this form. Its Team never draws the button; this is the
    // belt to that brace, e.g. if the company's template hasn't loaded yet.
    if (isContractor()) {
      import('./invite.js').then(({ openInvite: invite }) => invite({ session, company: orgInfo().name, onSent: () => renderTeam(mount, session) }));
      return;
    }
    // The company's own sign-in domains (ASP: aspconsulting.co.uk); none = any address.
    const domains = allowedDomainsOf({ signIn: orgSetting('signIn', null), signInDomain: orgSetting('signInDomain', null) });
    openDialog({
      title: 'Add person',
      subtitle: 'They get access the first time they sign in with this address.',
      width: 520,
      note: 'Invited by email',
      fields: [
        { key: 'name', label: 'Full name', type: 'text', span: true, required: true, placeholder: 'e.g. Priya Sharma' },
        {
          key: 'email', label: 'Work email', type: 'email', span: true, required: true,
          placeholder: `name@${domains[0] || 'company.co.uk'}`,
          validate: (v) => checkWorkEmail(v, domains),
        },
        {
          key: 'role', label: 'Role', type: 'select', required: true, value: 'Engineer',
          // "Display" is a screen on a wall, not a person: it carries the
          // `display` claim instead of `org`, which denies it every rule in the
          // application and admits it to the room wallboard only. Without this
          // option the meeting-room iPad had to be created as a Technician,
          // which is exactly what went wrong (Karan 18/09).
          options: ['Engineer', 'Coordinator', 'Technician', 'Director', 'Finance', 'Owner', 'Display']
            .map((r) => ({ value: r, label: r === 'Display' ? 'Display (a room screen, not a person)' : r })),
        },
        { key: 'rate', label: 'Charge-out', type: 'money', affix: '£', placeholder: '60' },
        { key: 'leave', label: 'Annual leave', type: 'number', optional: true, placeholder: '21', help: 'Days per year' },
      ],
      primary: {
        label: 'Add person',
        onSubmit: async (v) => {
          const newId = await addTeamMember({
            displayName: v.name, email: v.email, roles: [v.role],
            chargeOutRate: Number(v.rate) || null,
            leaveEntitlement: Number(v.leave) || null,
          });
          // The record admits nobody on its own — the claim does. Granting here
          // is what turns "added" into "can sign in", instead of leaving them on
          // the provisioning screen waiting for a sync that never looks here.
          await announceAccess(newId, v.email);
          renderRoster(mount, session, { onOpenLead, onOpenProject, editPatterns, headEl });
        },
      },
    });
  }

  // ── Invite (multi-tenant step 6) — anyone, by email or phone, with a role.
  // Off ASP's own address only; ASP adds people above by their work address.
  function openInvite() {
    openDialog({
      title: 'Invite someone',
      subtitle: 'They get a link. Opening it and signing in with the same email or phone number makes them a member.',
      width: 520,
      fields: [
        { key: 'name', label: 'Name', type: 'text', span: true, optional: true, placeholder: 'e.g. Sam Patel' },
        { key: 'email', label: 'Email', type: 'email', span: true, optional: true, placeholder: 'sam@example.com' },
        { key: 'phone', label: 'Or mobile number', type: 'text', span: true, optional: true, placeholder: '+44 7700 900123', help: 'International format, starting +' },
        { key: 'role', label: 'Role', type: 'select', required: true, value: 'Engineer',
          options: ['Engineer', 'Coordinator', 'Finance', 'Director', 'Owner'].map((r) => ({ value: r, label: r })) },
      ],
      primary: {
        label: 'Create invite',
        onSubmit: async (v) => {
          const r = await callFunction('createInvite', { name: v.name, email: v.email || '', phone: v.phone || '', roles: [v.role] });
          const link = `${location.origin}${r.link}`;
          try { await navigator.clipboard.writeText(link); } catch (_) { /* clipboard blocked: the prompt shows it */ }
          window.prompt('Invite link (copied). It works once and expires in 14 days:', link);
        },
      },
    });
  }

  // ── Person detail page (Benchmark type + components) ──
  const fmtD = (iso) => { if (!iso) return '—'; const d = new Date(iso + 'T00:00:00'); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); };
  function renderMember(u) {
    const emailLc = String(u.email || '').toLowerCase();
    const former = u.active === false;
    const util = former ? 0 : utilOf(u);
    const lb = leaveLeft(u);
    const uc = utilColour(util);
    const name = titleCase(u.displayName || u.id);
    const mine = projects.filter((p) => refTail(p.lead) === u.id);
    const liveMine = mine.filter((p) => ACTIVE_STATUSES.includes(p.status));
    const dueThisWeek = liveMine.filter((p) => { const d = p.due || p.dueDate; if (!d) return false; const dd = new Date(d); return dd >= now && (dd - now) <= 7 * 86400000; }).length;
    const schemes = [...new Set(liveMine.map((p) => p.scheme).filter(Boolean))];

    const cap = util >= overload() ? { word: 'Over capacity', note: `Running at ${util}% over the last month — new work will slip unless something moves.` }
      : util >= 55 ? { word: 'Balanced', note: `Workload is within range. ${liveMine.length} live project${liveMine.length === 1 ? '' : 's'} as lead${dueThisWeek ? `, ${dueThisWeek} due this week` : ''}.` }
      : util > 0 ? { word: 'Has room', note: `Running at ${util}% — capacity for more work.` }
      : { word: 'Has room', note: 'No tracked time in the last month.' };

    // 4-week time chart, scaled against 45h.
    const weekHrs = winWeeks.map((wi) => {
      let mins = 0; sheets.forEach((s) => { if (s.imported || String(s.userEmail || '').toLowerCase() !== emailLc || s.weekStart !== wi) return; (s.rows || []).forEach((r) => Object.values(r.days || {}).forEach((v) => { mins += parseHM(v); })); });
      return Math.round(mins / 60);
    });
    const thisHrs = weekHrs[3];

    const myLeave = leaveReqs.filter((r) => r.userEmail === u.email).sort((a, b) => String(b.from || '').localeCompare(String(a.from || ''))).slice(0, 6);
    const leaveDot = (r) => r.kind === 'cpd' ? 'var(--ink)' : (r.status === 'approved' && (r.to || r.from) < todayIso) ? 'var(--hair-strong)' : 'var(--brand-orange)';
    const leaveKind = (r) => leaveWord(r.kind) + (r.status === 'requested' ? ' · pending' : (r.status === 'approved' && (r.to || r.from) < todayIso) ? ' · taken' : '');

    const permission = (u.roles || []).some((r) => SENIOR.includes(r)) ? `${(u.roles || []).join(' · ')} — full financials` : 'Standard — no financials';
    const joined = u.joined || u.joinedYear || (u.createdAt ? new Date(u.createdAt).getFullYear() : null);
    const status = former ? { label: 'Inactive', tone: '' } : (onLeaveToday(u) ? { label: 'On leave', tone: 'warn' } : { label: 'Active', tone: 'ok' });

    // General figures card — no gating.
    const figsGeneral = bmFiguresCard({
      figures: [
        { value: former ? '—' : util + '%', label: 'Utilisation' },
        { value: String(liveMine.length), label: 'Live as lead' },
        { value: `${lb.remaining}`, label: `Leave left of ${lb.entitlement}` },
        { value: thisHrs + 'h', label: 'Tracked this week' },
      ],
    });
    // Financial figures card — SENIORS ONLY (isSenior is the role gate). Charge-out,
    // break-even cost and annual salary are all sensitive: each value is wrapped in
    // secretFig so the global top-bar Figures toggle masks them. Built inline (NOT via
    // bmFiguresCard) because that component escapes its value — which would print the
    // secretFig spans as literal text — so the same bm-* markup is reproduced here.
    const finFig = (valueHtml, i, label) => `
      <div class="bm-cfig" style="animation-delay:${200 + 40 * i}ms">
        <div class="bm-figure">${valueHtml}</div>
        <div class="bm-figure-label">${escapeHtml(label)}</div>
      </div>`;
    const figsFin = isSenior ? `<div class="bm-card bm-in-rise" style="animation-delay:80ms">
      <div class="bm-card-figures">
        ${finFig(secretFig(u.chargeOutRate ? `£${Number(u.chargeOutRate)}/hr` : '—', isSenior), 0, 'Charge-out')}
        ${finFig(secretFig(rates[u.id] ? `£${Number(rates[u.id])}/hr` : '—', isSenior), 1, 'Break-even cost')}
        ${finFig(secretFig(salaries[u.id] ? GBP(salaries[u.id]) : '—', isSenior), 2, 'Annual salary')}
      </div>
    </div>` : '';

    const projTable = liveMine.length ? bmTable({
      head: [{ label: 'Ref' }, { label: 'Client' }, { label: 'Scheme' }, { label: 'Status' }],
      groups: [{
        rows: liveMine.slice(0, 20).map((p) => ({
          id: p.id,
          cells: [
            { text: p.number || '—', mono: true },
            { html: `<div>${escapeHtml(p.clientName || p.title || '—')}</div>` },
            { html: `<div class="bm-meta">${escapeHtml(p.scheme || '—')}</div>` },
            { html: statusCell(p.status) },
          ],
        })),
      }],
    }) : '<p class="bm-body muted" style="margin-top:10px">No live projects led by this person.</p>';

    const secGap = 'margin-top:30px';
    // Edit-patterns 6b hover affordance. The hover is the ONLY affordance at rest
    // (§01: "a row is not obviously editable and should not be"): the row tints
    // --brand-blue-50 bled 10px each side and the word EDIT (never a pencil) fades
    // in right-aligned in ~120ms. Suppressed while a save badge is showing.
    const epStyle = editPatterns ? `<style>
      .ep-editable:hover{background:var(--nav-hover);box-shadow:-10px 0 0 var(--halo),10px 0 0 var(--halo);cursor:pointer}
      .ep-editable:hover .ep-edit{opacity:1}
      .ep-saved .ep-edit{opacity:0 !important}
    </style>` : '';
    mount.innerHTML = `${epStyle}
      <nav class="crumbs" aria-label="Breadcrumb"><button type="button" id="pm-back">Team</button><span>/ ${escapeHtml(name)}</span></nav>
      ${lockBarHtml}

      <div style="display:flex;align-items:flex-start;gap:16px;flex-wrap:wrap;margin-top:18px">
        <span class="mini-avatar" style="width:52px;height:52px;font-size:18px;flex:none">${escapeHtml(initials(u.displayName))}</span>
        <div style="flex:1;min-width:220px">
          <div class="bm-eyebrow blue">${escapeHtml((u.roles || [])[0] || '—')}</div>
          <div class="bm-anchor md" style="margin-top:4px">${escapeHtml(name)}</div>
          <div class="bm-meta" style="margin-top:6px">${joined ? `Joined ${escapeHtml(String(joined))}` : ''}${joined && u.email ? ' · ' : ''}${u.email ? `<a href="mailto:${escapeHtml(u.email)}">${escapeHtml(u.email)}</a>` : ''}</div>
        </div>
        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:10px">
          ${pill(status.label, status.tone)}
        </div>
      </div>

      <div style="${secGap}">${bmBrief(cap.note, { eyebrow: `Capacity — ${cap.word}` })}</div>

      <div style="${secGap}">${figsGeneral}</div>
      ${isSenior ? `<div style="margin-top:16px">${figsFin}</div>` : ''}

      ${editPatterns ? `<div style="${secGap}">
        <div class="bm-eyebrow blue">Details</div>
        <div style="margin-top:10px">
          <div id="pm-charge"></div>
          <div id="pm-charge-note" style="margin-top:8px"></div>
        </div>
      </div>` : ''}

      <div style="${secGap}">
        <div style="display:flex;align-items:baseline;gap:12px">
          <div class="bm-eyebrow blue">Projects as lead</div>
          <span class="bm-meta">${liveMine.length}</span>
          <span style="flex:1"></span>
          ${onOpenLead && mine.length ? `<button class="btn ghost" id="pm-viewlead">View all as lead →</button>` : ''}
        </div>
        <div id="pm-proj" style="margin-top:10px">${projTable}</div>
      </div>

      ${canAssets() ? `<div style="${secGap}" id="pm-kit-wrap">
        <div style="display:flex;align-items:baseline;gap:12px">
          <div class="bm-eyebrow blue">${former ? 'Kit to collect' : 'Kit'}</div>
          <span class="bm-meta" id="pm-kit-n"></span>
        </div>
        <div id="pm-kit" style="margin-top:10px"><p class="bm-body muted" style="margin:0">Loading…</p></div>
      </div>` : ''}

      <div style="${secGap}">
        <div class="bm-eyebrow blue">Time — last four weeks</div>
        <div style="display:flex;align-items:flex-end;gap:16px;height:120px;margin-top:14px">
          ${weekHrs.map((h, i) => `<div style="display:flex;flex-direction:column;align-items:center;gap:6px;flex:1;justify-content:flex-end">
            <span class="bm-mono" style="font-size:12px;color:var(--ink-2)">${h}</span>
            <span style="width:100%;max-width:48px;height:${Math.min(108, Math.round((h / 45) * 108)) + 2}px;background:${i === 3 ? 'var(--brand-blue)' : 'var(--anchor)'};border-radius:3px 3px 0 0"></span>
          </div>`).join('')}
        </div>
        <div style="display:flex;gap:16px;margin-top:6px">${['W-3', 'W-2', 'W-1', 'This'].map((l) => `<span class="bm-meta" style="flex:1;text-align:center">${l}</span>`).join('')}</div>
      </div>

      <div style="${secGap}">
        <div class="bm-eyebrow blue">Timesheets</div>
        ${(() => { // this person's sheets, newest first (Karan 08/08: "I should be able to see all timesheets this user has submitted here")
    const mine = sheets.filter((sh) => sh.userEmail === u.email)
      .sort((a, b) => String(b.weekStart || '').localeCompare(String(a.weekStart || ''))).slice(0, 10);
    const mins = (sh) => (sh.rows || []).reduce((t, r) => t + Object.values(r.days || {}).reduce((x, v) => {
      const sv = String(v || '').trim(); if (!sv) return x;
      if (sv.includes(':')) { const [h2, m2] = sv.split(':'); return x + (parseInt(h2 || 0, 10) * 60) + parseInt(m2 || 0, 10); }
      const f = parseFloat(sv); return x + (isNaN(f) ? 0 : Math.round(f * 60));
    }, 0), 0);
    const hm2 = (m) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
    const stLabel = (st) => st === 'rejected' ? 'Sent back' : titleCase(st || 'draft');
    const stCol = (st) => st === 'approved' ? 'var(--status-ok)' : st === 'submitted' ? 'var(--link)' : st === 'rejected' ? 'var(--attn-ink)' : 'var(--ink-3)';
    return mine.length ? mine.map((sh) => `
      <div style="display:flex;align-items:baseline;gap:14px;padding:8px 0;border-bottom:1px solid var(--hair);font-size:13px">
        <span class="bm-mono" style="width:88px">${escapeHtml(String(sh.weekStart || '—'))}</span>
        <span class="bm-mono" style="width:52px;text-align:right">${hm2(mins(sh))}</span>
        <span style="font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:${stCol(sh.status)}">${escapeHtml(stLabel(sh.status))}</span>
        <span style="flex:1"></span>
        <span style="font-size:11.5px;color:var(--ink-3)">${sh.submittedAt ? `submitted ${escapeHtml(new Date(sh.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }))}` : ''}</span>
      </div>`).join('') : '<p class="g-note" style="margin-top:8px">No timesheets on record (the list covers the most recent 600 sheets).</p>';
  })()}
      </div>

      <div style="${secGap}">
        <div class="bm-eyebrow blue">Access</div>
        <div style="display:flex;justify-content:space-between;gap:16px;padding:9px 0;border-bottom:1px solid var(--hair)"><span class="bm-meta">Sign-in</span><span class="bm-body">${u.email ? escapeHtml(u.email) : '—'}</span></div>
        ${editPatterns
        // 6e permission enum pill (§7 item 6). Owner-only; changing it alters what
        // OTHERS can see (orange node + reason). §8-adjacent SIMPLIFICATION (flag for
        // Karan): ASP conflates job title and access role in u.roles, so this pill
        // reduces permission to a two-value model — Standard (no finance) vs Full
        // financials — mapped by adding/removing a finance-capable role (Owner ·
        // Director · Finance). It does not model the full role vocabulary.
        ? `<div style="display:flex;justify-content:space-between;align-items:center;gap:16px;padding:9px 0;border-bottom:1px solid var(--hair)"><span class="bm-meta">Permission</span><span id="pm-perm-pill"></span></div>`
        : `<div style="display:flex;justify-content:space-between;gap:16px;padding:9px 0;border-bottom:1px solid var(--hair)"><span class="bm-meta">Permission</span><span class="bm-body">${escapeHtml(permission)}</span></div>`}
        <div class="tm-ts-row" style="padding:9px 0;border-bottom:1px solid var(--hair)">
          <div style="display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap"><span class="bm-meta">Timesheets</span><span id="pm-ts">${timesheetToggleHtml(u)}</span></div>
          <p class="bm-meta" style="margin:6px 0 0">Not required: they can still log time, but they’re never chased or counted as missing. The Tuesday 10:00 lock still applies.</p>
          <p id="pm-ts-err" class="bm-meta" role="status" style="margin:4px 0 0;color:var(--status-error)" hidden></p>
        </div>
        <div class="tm-rs-row" style="padding:9px 0;border-bottom:1px solid var(--hair)">
          <div style="display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap"><span class="bm-meta">Room screen</span><span id="pm-rs">${roomScreenToggleHtml(u)}</span></div>
          <p class="bm-meta" style="margin:6px 0 0">Yes: this account runs the meeting-room display. It keeps its permission (e.g. Coordinator) but isn’t counted as a person — not in meetings, Who’s where, approvals or counts.</p>
          ${isRoomScreen(u) ? '<div style="margin-top:8px"><button type="button" class="btn ghost" id="pm-rs-open">Open room display</button></div>' : ''}
          <p id="pm-rs-err" class="bm-meta" role="status" style="margin:4px 0 0;color:var(--status-error)" hidden></p>
        </div>
        <div style="display:flex;justify-content:space-between;gap:16px;padding:9px 0;border-bottom:1px solid var(--hair)"><span class="bm-meta">Last updated</span><span class="bm-body">${u.updatedAt ? new Date(u.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</span></div>
      </div>

      ${hasFeature('signatures') && sigCfg && !former ? `<div style="${secGap}" id="pm-sig"></div>` : ''}

      <div style="${secGap}">
        <div class="bm-eyebrow blue">Leave</div>
        ${myLeave.length ? myLeave.map((r) => `<div style="display:flex;align-items:center;gap:12px;padding:8px 0;border-bottom:1px solid var(--hair)">
          <span style="width:8px;height:8px;border-radius:50%;background:${leaveDot(r)};flex:none"></span>
          <span class="bm-mono" style="font-size:12px;min-width:96px">${fmtD(r.from)}${r.to && r.to !== r.from ? ` – ${fmtD(r.to)}` : ''}</span>
          <span class="bm-meta">${escapeHtml(leaveKind(r))}</span></div>`).join('') : '<p class="bm-body muted" style="margin-top:8px">No leave booked.</p>'}
      </div>

      <div style="${secGap}">${bmBrief(
        `${firstName(u.displayName) || 'They'} tracked ${thisHrs}h this week${schemes.length ? ` across ${schemes.length} scheme${schemes.length === 1 ? '' : 's'}` : ''}. ${util >= overload() ? `Utilisation is high at ${util}% against a ${overload()}% target.` : util >= 55 ? `Utilisation is steady at ${util}% against a ${overload()}% target.` : util > 0 ? `Utilisation is light at ${util}% against a ${overload()}% target.` : 'No tracked time in the last four weeks.'}`,
        { eyebrow: 'Track summary' })}</div>

      ${isSenior && !former ? `<div style="display:flex;gap:12px;${secGap}">
        <button class="btn ghost" id="pm-edit">Edit details</button>
        ${canOffboard ? '<button class="btn ghost" id="pm-off">Mark as former…</button>' : ''}</div>` : ''}
      ${former && canOffboard ? `<div style="display:flex;gap:12px;${secGap}">
        <button class="btn ghost" id="pm-on">Make active again…</button></div>` : ''}

      ${editPatterns ? `<div style="${secGap}">
        <span style="font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--brand-orange)">Changes to this record</span>
        <div id="pm-changes" style="margin-top:14px"><p class="bm-body muted" style="margin:0">Loading…</p></div>
      </div>` : ''}`;

    mount.querySelector('#pm-back').addEventListener('click', () => renderRoster(mount, session, { onOpenLead, onOpenProject, editPatterns, headEl }));
    bmTableNav(mount.querySelector('#pm-proj'), (id) => { if (onOpenProject) onOpenProject(id); });
    const viewLead = mount.querySelector('#pm-viewlead');
    if (viewLead) viewLead.addEventListener('click', () => onOpenLead(u.id, name));
    const pmEdit = mount.querySelector('#pm-edit');
    if (pmEdit) pmEdit.addEventListener('click', () => openEdit(u));
    const pmOff = mount.querySelector('#pm-off');
    // 6g ending confirm — one path, shared with the ⋯ row menu. Replaces the OLD
    // inline confirm()/updateUserFields offboard so there is a single offboard flow.
    if (pmOff) pmOff.addEventListener('click', () => markAsFormer(u));
    const pmOn = mount.querySelector('#pm-on');
    if (pmOn) pmOn.addEventListener('click', () => makeActiveAgain(u));
    wireTimesheetToggle(u);
    wireRoomScreenToggle(u);
    // Email signature (v3, 31/10/2026): a read-only card — their signature as it
    // renders now, in a sandboxed frame. On your OWN record it offers the three
    // facts you may correct yourself (the rules allow exactly those).
    const sigHost = mount.querySelector('#pm-sig');
    if (sigHost) {
      const mineRec = String(u.email || '').toLowerCase() === String(session.email || '').toLowerCase();
      const paintSig = () => mountSignatureCard(sigHost, { cfg: sigCfg, user: u, onEditMine: mineRec ? editMine : null });
      function editMine() {
        openPanel({
          title: 'Your email signature',
          subtitle: 'Your role is set by the Owner or a Director. These are yours to correct.',
          fields: [
            { key: 'mobile', label: 'Mobile', type: 'text', optional: true, value: u.mobile || '', placeholder: '+44 7700 900123', help: 'Left out of the signature when blank.' },
            { key: 'officeTel', label: 'Office number', type: 'text', optional: true, value: u.officeTel || '', placeholder: sigCfg.officeTel || '', help: 'Only if yours is not the company number — e.g. the India office. Include the country code.' },
            { key: 'preNominals', label: 'Pre-nominals', type: 'text', optional: true, value: u.preNominals || '', placeholder: 'e.g. Eur Ing', help: 'Before your name, in its own case.' },
            { key: 'postNominals', label: 'Post-nominals', type: 'text', optional: true, value: u.postNominals || '', placeholder: 'e.g. BSc (Hons) MIED', help: 'After your name, in its own case.' },
          ],
          primary: {
            label: 'Save',
            onSubmit: async (v) => {
              const mobile = ukMobile(v.mobile);
              const patch = {
                mobile, mobileE164: toE164(mobile) || null,
                officeTel: String(v.officeTel || '').trim(), preNominals: String(v.preNominals || '').trim(), postNominals: String(v.postNominals || '').trim(),
                updatedBy: session.email, updatedAt: new Date().toISOString(),
              };
              await updateUserFields(u.id, patch);
              Object.assign(u, patch);
              paintSig();
            },
          },
        });
      }
      paintSig();
    }
    // Assets mission 4 — Kit: what this person has (assignedTo == them). Read-only: reassigning is on the asset.
    // A new starter reads it as their Kit line; a former member as what to collect.
    if (canAssets()) loadKit(u.id).then((kit) => {
      const box = mount.querySelector('#pm-kit');
      if (!box) return;
      const n = mount.querySelector('#pm-kit-n');
      if (n) n.textContent = kit.length ? String(kit.length) : '';
      const lines = former ? collectLines(kit) : null;
      box.innerHTML = kit.length
        ? `<p class="bm-meta" style="margin:0 0 6px">${escapeHtml(former ? `${lines.length} item${lines.length === 1 ? '' : 's'} to collect` : kitLine(kit))}</p>`
          + kit.map((k) => `<div class="as-kit-row"><span class="as-kit-num">${escapeHtml(k.number)}</span><span>${escapeHtml(k.name)}</span>${k.critical ? '<span class="as-kit-crit">Critical</span>' : ''}<span class="as-kit-meta">${escapeHtml(k.status.replace(/-/g, ' '))}</span></div>`).join('')
        : `<p class="bm-body muted" style="margin:0">${former ? 'Nothing to collect.' : 'Kit: nothing assigned yet. Assign an item from the asset itself.'}</p>`;
    }).catch(() => {});

    // ── Edit-patterns wiring (pilot gate only) ──────────────────────────────
    // 6b inline charge-out field + 6h "Changes to this record" list. Function
    // declarations are hoisted, so the calls above their definitions are fine.
    if (editPatterns) { initChargeField(); renderChangeList(); initPermissionPill(); }

    // 6e permission enum pill (§7 item 6). Owner-only; changing permission alters
    // what OTHERS can see → orange node + a required reason. Not-Owner viewers get
    // an inert pill with "Owner" named (never a disabled control). The two-value
    // permission model is a §8-adjacent simplification — see the Access comment.
    function initPermissionPill() {
      const host = mount.querySelector('#pm-perm-pill');
      if (!host) return;
      const isOwner = session.roles.includes('Owner');
      const permValue = (u.roles || []).some((r) => SENIOR.includes(r)) ? 'finance' : 'standard';
      const permLabel = (v) => v === 'finance' ? 'Full financials' : 'Standard — no finance';
      // standard ↔ finance maps to removing / adding a finance-capable role.
      const rolesFor = (v) => {
        const cur = (u.roles || []).slice();
        if (v === 'finance') return cur.some((r) => SENIOR.includes(r)) ? cur : cur.concat(['Finance']);
        const stripped = cur.filter((r) => !SENIOR.includes(r));
        return stripped.length ? stripped : ['Engineer'];
      };
      renderPill(host, {
        value: permValue,
        disabled: !isOwner,
        gateRole: isOwner ? undefined : 'Owner',
        options: [
          { value: 'standard', label: 'Standard — no finance', consequence: 'Cannot see charge-out, cost or salary' },
          { value: 'finance', label: 'Full financials', consequence: 'Can see and edit money fields', needsReason: true },
        ],
        onChange: async (newValue, reason) => {
          if (newValue === permValue) return;
          const at = new Date().toISOString();
          const roles = rolesFor(newValue);
          await updateUserFields(u.id, { roles, updatedBy: session.email, updatedAt: at });
          await addRecordChange({ record: 'users/' + u.id, field: 'permission', label: 'Permission', from: permLabel(permValue), to: permLabel(newValue), by: session.email, byName: session.displayName, at, reason: reason || null, money: false });
          u.roles = roles; u.updatedBy = session.email; u.updatedAt = at;
          renderMember(u); // re-render so the pill value + change list reflect the write
        },
      });
    }

    // Deliverable A — the ONE inline field (charge-out), all five states of 6b.
    function initChargeField() {
      const host = mount.querySelector('#pm-charge');
      const noteBox = mount.querySelector('#pm-charge-note');
      if (!host) return;
      let curRate = (u.chargeOutRate != null && u.chargeOutRate !== '') ? Number(u.chargeOutRate) : null;

      // 01 · REST — label (150px / 13px / grey-500) + value in Monitor. No border,
      // no underline, no icon. The row height fixed by the rest padding (13px top
      // + 13px bottom); every other state is made to match it.
      const LABEL = '<span style="width:150px;flex:none;font-size:13px;color:var(--ink-3)">Charge-out</span>';
      const REST_STYLE = 'display:flex;align-items:center;gap:16px;padding:13px 10px 13px 0;border-bottom:1px solid var(--hair-soft);font-size:14.5px';
      const valRest = () => curRate == null
        ? '<span class="ep-value" style="font-family:var(--font-technical);flex:1;color:var(--ink-2)">—</span>'
        : `<span class="ep-value" style="font-family:var(--font-technical);flex:1">&#163;${escapeHtml(Number(curRate).toFixed(2))} / hr</span>`;
      const EDIT_WORD = '<span class="ep-edit" style="font-size:11px;font-weight:500;letter-spacing:0.09em;text-transform:uppercase;color:var(--link);opacity:0;transition:opacity 120ms var(--ease-out)">Edit</span>';
      const note = (msg) => { if (noteBox) noteBox.innerHTML = msg ? `<span style="font-size:12px;color:var(--brand-orange)">${escapeHtml(msg)}</span>` : ''; };

      // 05 · REFUSED — !isSenior (§6: Charge-out is Owner · Director · Finance). No
      // hover, no cursor change, inset orange rule, value in grey-700, and the
      // holding role named at the right in 10.5px caps ("Director", per 6b). NEVER
      // a disabled grey input.
      // §5 TENSION (flag for Karan): money gating would normally MASK the figure
      // from a non-senior entirely; the 6b refused state shows it in grey-700, so
      // that is rendered here. Pilot gate is off by default, so nothing leaks live.
      if (!isSenior) {
        host.innerHTML = `<div class="ep-field" style="display:flex;align-items:center;gap:16px;padding:13px 0 13px 12px;border-bottom:1px solid var(--hair-soft);font-size:14.5px;box-shadow:inset 3px 0 0 var(--brand-orange)">
          <span style="width:138px;flex:none;font-size:13px;color:var(--ink-3)">Charge-out</span>
          <span style="font-family:var(--font-technical);flex:1;color:var(--ink-2)">${curRate == null ? '—' : '&#163;' + escapeHtml(Number(curRate).toFixed(2)) + ' / hr'}</span>
          <span style="font-size:10.5px;font-weight:500;letter-spacing:0.09em;text-transform:uppercase;color:var(--ink-3)">Director</span>
        </div>`;
        return;
      }

      let editing = false, escaped = false, done = false;

      const paintRest = () => {
        editing = false;
        host.innerHTML = `<div class="ep-field ep-editable" style="${REST_STYLE}">${LABEL}${valRest()}${EDIT_WORD}</div>`;
        const row = host.firstElementChild;
        row.addEventListener('click', (e) => { if (e.target.closest('input')) return; paintEditing(row); });
      };

      // 03 · EDITING — the value is replaced IN PLACE by an <input> (Monitor,
      // because it's a rate). £ and "/ hr" stay OUTSIDE the input; only the number
      // is edited. The label does not move and — the #1 requirement — the ROW DOES
      // NOT CHANGE HEIGHT: we measure the rest height and PIN it before the swap,
      // and also drop the row's vertical padding 13→7px exactly as the artboard
      // does. Pinning makes "no shift" true regardless of font metrics.
      const paintEditing = (row) => {
        if (editing) return;
        editing = true; escaped = false; done = false; note('');
        const restH = row.offsetHeight;
        row.className = 'ep-field'; // drop ep-editable → no hover tint while editing
        // Pin height (border-box) so the row can't grow; NO overflow:hidden, so the
        // input's focus halo renders in full — a fixed-height flex row doesn't grow
        // to fit a taller item, it just centres it, so nothing below ever moves.
        row.setAttribute('style', `${REST_STYLE};padding-top:7px;padding-bottom:7px;height:${restH}px;box-sizing:border-box`);
        row.innerHTML = `${LABEL}<span style="flex:1;display:flex;align-items:center;gap:10px;font-family:var(--font-technical)">
          <span style="color:var(--ink-2)">&#163;</span>
          <input id="pm-charge-input" type="text" inputmode="decimal" autocomplete="off" value="${curRate == null ? '' : escapeHtml(String(curRate))}" style="flex:1;min-width:0;border:1px solid var(--link);border-radius:8px;padding:8px 11px;font-family:var(--font-technical);font-size:14px;background:var(--surface);box-shadow:0 0 0 3px var(--halo)">
          <span style="color:var(--ink-2)">/ hr</span>
          <span style="font-size:11px;letter-spacing:0.06em;color:var(--ink-3);white-space:nowrap">&#8629; SAVE &#183; ESC</span>
        </span>`;
        const input = row.querySelector('#pm-charge-input');
        input.focus(); input.select();
        // Commit on Enter AND on blur. Abandon on Escape. No Save button.
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(input.value); }
          else if (e.key === 'Escape') { e.preventDefault(); escaped = true; input.blur(); }
        });
        input.addEventListener('blur', () => { if (escaped) { escaped = false; paintRest(); return; } commit(input.value); });
      };

      const commit = async (raw) => {
        if (done) return; done = true; // guard: Enter then the removal-blur must not double-write
        const t = String(raw).trim();
        const newNum = t === '' ? null : Number(t);
        if (t !== '' && (!isFinite(newNum) || newNum < 0)) { // validate ≥ 0
          note('Enter a rate of 0 or more.'); done = false;
          const input = host.querySelector('#pm-charge-input'); if (input) input.focus();
          return;
        }
        const oldNum = curRate == null ? null : Number(curRate);
        if (newNum === oldNum) { paintRest(); return; } // unchanged → no write, no change-list line
        const at = new Date().toISOString();
        try {
          // §8.2 concurrent edits (unresolved): LAST-WRITE-WINS here — no record
          // lock. Two people on the same field both land, each writing its own
          // change-list line; the later write stands.
          await updateUserFields(u.id, { chargeOutRate: newNum, updatedBy: session.email, updatedAt: at });
          await addRecordChange({ record: 'users/' + u.id, field: 'chargeOutRate', label: 'Charge-out', from: oldNum, to: newNum, by: session.email, byName: session.displayName, at, money: true });
          curRate = newNum; u.chargeOutRate = newNum;
          paintCommitted();   // 04 · COMMITTED — SAVED badge IN the row, no toast
          renderChangeList(); // 6h list gains its line at THIS moment
        } catch (err) {
          // Write failed → revert value, small inline note, no toast.
          note('Couldn’t save — ' + (err && err.message || err));
          paintRest();
        }
      };

      // 04 · COMMITTED — value returns as £<new>.00 / hr with a 7px blue node + the
      // word SAVED (11px caps grey) for 2000ms, then both fade (200ms). No toast.
      const paintCommitted = () => {
        editing = false;
        host.innerHTML = `<div class="ep-field ep-editable ep-saved" style="${REST_STYLE}">${LABEL}${valRest()}
          <span class="ep-saved-badge" style="display:flex;align-items:center;gap:7px;transition:opacity 200ms var(--ease-out)">
            <span style="width:7px;height:7px;border-radius:50%;background:var(--brand-blue)"></span>
            <span style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--ink-3)">Saved</span>
          </span>${EDIT_WORD}</div>`;
        const row = host.firstElementChild;
        row.addEventListener('click', (e) => { if (e.target.closest('input')) return; paintEditing(row); });
        const badge = row.querySelector('.ep-saved-badge');
        setTimeout(() => { if (badge) badge.style.opacity = '0'; setTimeout(() => { if (host.querySelector('.ep-saved')) paintRest(); }, 200); }, 2000);
      };

      paintRest();
    }

    // 6h date column: "today 14:52" / "14 May" / "2019".
    function fmtChangeDate(iso) {
      if (!iso) return '';
      const d = new Date(iso); if (isNaN(d.getTime())) return '';
      const nowD = new Date();
      if (d.toDateString() === nowD.toDateString()) return 'today ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      if (d.getFullYear() === nowD.getFullYear()) return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      return String(d.getFullYear());
    }

    // Deliverable B — the change list (6h) at the foot of the record.
    function renderChangeList() {
      const box = mount.querySelector('#pm-changes');
      if (!box) return;
      // Value spans: Monitor when the field is monetary; new value in weight 500.
      const valSpan = (v, money, isNew) => {
        const txt = money ? (v == null ? '—' : '£' + Number(v).toFixed(2)) : (v == null || v === '' ? '—' : String(v));
        const css = `${money ? 'font-family:var(--font-technical);' : ''}${isNew ? 'font-weight:500;' : (money ? '' : 'color:var(--ink-2);')}`;
        return `<span style="${css}">${escapeHtml(txt)}</span>`;
      };
      const line = (c, recent, last) => {
        // Node colour (Ledger law): most-recent = blue in a --brand-blue-100 halo;
        // orange where the change carried a reason OR altered what others can see
        // (permission/visibility); navy for every earlier change. Precedence: the
        // most-recent node stays blue even if it carried a reason, matching the 6h
        // "most recent change" swatch.
        const reasonOrPerm = !!c.reason || c.field === 'permission' || c.field === 'visibility';
        const nodeColor = recent ? 'var(--brand-blue)' : reasonOrPerm ? 'var(--brand-orange)' : 'var(--ink)';
        const halo = recent ? 'box-shadow:0 0 0 3px var(--halo);' : '';
        // §5 money gating: a non-senior viewer sees a monetary change as
        // "<Label> changed" with NO figures. §8.4 (unresolved): whether Team
        // permission sees this list at all is undecided — here it shows non-money
        // lines plus masked money lines.
        const masked = !isSenior && c.money;
        let bodyHtml;
        if (c.baseline) bodyHtml = 'Record created';
        else if (masked) bodyHtml = `${escapeHtml(c.label || c.field || 'Field')} changed`;
        else bodyHtml = `${escapeHtml(c.label || c.field || 'Field')} ${valSpan(c.from, c.money, false)} &#8594; ${valSpan(c.to, c.money, true)}${c.reason ? ` &#8212; <span style="color:var(--ink-2)">${escapeHtml(c.reason)}</span>` : ''}`;
        return `<div style="display:flex;gap:16px;padding:13px 0;${last ? '' : 'border-bottom:1px solid var(--hair-soft);'}">
          <span style="width:11px;flex:none;display:flex;justify-content:center;padding-top:5px"><span style="width:9px;height:9px;border-radius:50%;background:${nodeColor};${halo}"></span></span>
          <span style="flex:1;font-size:13.5px;line-height:1.5">${bodyHtml}</span>
          <span style="font-size:12.5px;color:var(--ink-3);flex:none">${escapeHtml(c.byName || c.by || '—')}</span>
          <span style="font-family:var(--font-technical);font-size:12px;color:var(--ink-3);flex:none;width:88px;text-align:right">${escapeHtml(fmtChangeDate(c.at))}</span>
        </div>`;
      };

      listRecordChanges('users/' + u.id).then((changes) => {
        const all = (changes || []).slice(); // newest first (db.js sorts)
        // Baseline "Record created" — ONLY from a real timestamp, never fabricated.
        const baseIso = u.createdAt ? new Date(u.createdAt).toISOString() : (joined ? `${joined}-01-01T00:00:00.000Z` : null);
        if (baseIso) all.push({ baseline: true, label: 'Record created', at: baseIso, byName: '' });
        if (!all.length) { box.innerHTML = '<p class="bm-body muted" style="margin:0">No changes recorded yet.</p>'; return; }
        let expanded = false;
        const paint = () => {
          const shown = expanded ? all : all.slice(0, 6);
          const rows = shown.map((c, i) => line(c, i === 0 && !c.baseline, i === shown.length - 1)).join('');
          const more = (!expanded && all.length > 6)
            ? `<span id="pm-changes-more" style="display:inline-block;margin-top:14px;font-size:12.5px;font-weight:500;color:var(--link);cursor:pointer">Show all ${all.length}</span>` : '';
          box.innerHTML = `<div style="display:flex;flex-direction:column">${rows}</div>${more}`;
          const m = box.querySelector('#pm-changes-more');
          if (m) m.addEventListener('click', () => { expanded = true; paint(); });
        };
        paint();
      }).catch(() => { box.innerHTML = '<p class="bm-body muted" style="margin:0">Couldn’t load changes.</p>'; });
    }
  }

  draw();
}
